import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BoxedImage, ImageBox, type Crop } from '../../../components/BoxedImage';
import { CameraImageViewer } from '../../../components/CameraImageViewer';
import { ImageViewer } from '../../../components/ImageViewer';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import { ImageZoomModal } from '../../../pages/embeddings/components/ImageZoomModal';
import { ApiService } from '../../../services/api';
import {
  CameraDetails,
  CameraImageResponse,
  CameraImageTimelineResponse,
  CameraPog,
  PogBBox,
  StoreProductSearchResult,
} from '../../../types';
import { CandidatePreviewCard, safeImageUrl } from '../../components/common/CandidatePreviewCard';
import { CropActionBar } from '../../components/common/CropActionBar';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import {
  reviewIssueActionFromShortcutKey,
  ReviewIssueChooserOverlay,
} from '../../components/common/ReviewIssueChooserOverlay';
import { UPCColumnView } from '../../components/common/UPCColumnView';
import { useMasterLabelEditor } from '../../hooks/useMasterLabelEditor';
import { useZoomPreview, ZoomMeta } from '../../hooks/useZoomPreview';
import { FORGE_MASTER_LABEL_PATH, FORGE_REMOTE_DC_TOOL_PATH } from '../../paths';
import { ForgeApiError, ForgeApiService } from '../../services/ForgeApiService';
import { buildMasterLabelDebugInfo } from '../../services/masterLabelState';
import { transformApiUPCData } from '../../services/masterLabelTransforms';
import {
  ApiMasterLabelUPCData,
  MasterBarcodeEntry,
  MasterCropData,
  MasterDataCollectionDetailResponse,
  MasterDataCollectionImagePreviewResponse,
  MasterDataCollectionRemoteCropMaterialization,
  MasterLabelReviewIssueType,
  MasterLabelStateSaveRequest,
  MasterUPCData,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionDetection,
  RemoteDataCollectionImageTimelineResponse,
} from '../../types';
import { resolveDraftDesignVerifiedBarcode } from '../../utils/masterBarcodeRows';
import {
  MASTER_LABEL_IMAGE_SLIDER_MAX,
  MASTER_LABEL_IMAGE_SLIDER_MIN,
} from '../../utils/masterLabelImageSizing';
import {
  REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT,
  REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
  REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
  REMOTE_DC_DEFAULT_MERGE_LINKED_CANDIDATES,
  REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SAMPLE_SIZE,
  REMOTE_DC_DEFAULT_SEARCH_SIZE,
  REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT,
} from '../../utils/remoteDataCollectionDefaults';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const STORAGE_KEYS = {
  polygonStrokeWidth: 'forge-master-data-collection-polygon-stroke-width',
  overlayOpacityPct: 'forge-master-data-collection-overlay-opacity-pct',
  imagePanePct: 'forge-master-data-collection-image-pane-pct',
  autoImageArea: 'forge-master-data-collection-auto-image-area',
  workspaceZoomPct: 'forge-master-data-collection-workspace-zoom-pct',
  cropSizePx: 'forge-crop-size',
  viewControlsMode: 'forge-master-data-collection-view-controls-mode',
  autoCloseMasterLabelOnSave: 'forge-master-data-collection-auto-close-master-label-on-save',
  autoSelectCameraCrops: 'forge-master-data-collection-auto-select-camera-crops',
};

const POLYGON_STROKE_RANGE = { min: 1, max: 32 };
const OVERLAY_OPACITY_RANGE = { min: 0, max: 100 };
const IMAGE_PANE_RANGE = { min: 15, max: 98 };
const WORKSPACE_ZOOM_RANGE = { min: 70, max: 115 };
const SSM_CONFIDENCE_RANGE = { min: 0, max: 100 };
const REMOTE_DC_CROP_SIZE_PX = 132;
const REMOTE_DC_DRAFT_CROP_PREVIEW_SIZE_PX = 320;
const REMOTE_DC_DRAFT_CROP_PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="320" viewBox="0 0 320 320"%3E%3Crect width="320" height="320" fill="%23000"/%3E%3C/svg%3E';
const REMOTE_DC_PRODUCT_COLOR = '#2f81f7';
const REMOTE_DC_CARTON_COLOR = '#a855f7';
const REMOTE_DC_CAMERA_VIEWPORT_CHROME_PX = 190;
const REMOTE_DC_MIN_POG_PANE_WIDTH = 320;
const REMOTE_DC_FALLBACK_CAMERA_ASPECT_RATIO = 4 / 3;
const EMPTY_REMOTE_CROP_IDS: string[] = [];

function fallbackRemoteDcDefaults(): RemoteDataCollectionDefaults {
  return {
    searchSize: REMOTE_DC_DEFAULT_SEARCH_SIZE,
    sampleSize: REMOTE_DC_DEFAULT_SAMPLE_SIZE,
    similarityThreshold: REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT / 100,
    deltaThreshold: REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT / 100,
    ssmConfidenceThreshold: REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT / 100,
    mergeLinkedCandidates: REMOTE_DC_DEFAULT_MERGE_LINKED_CANDIDATES,
    lowEmbeddingThreshold: REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
    includeSimilarityPriority: REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
    includeDeltaPriority: REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
    similarityPriorityWeight: REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
    deltaPriorityWeight: REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
    lowEmbeddingPriorityWeight: REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
    productPriorityWeight: REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
    cartonPriorityWeight: REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
    detectedPolygonNormalization: REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT / 100,
  };
}

export type DetectionTypeFilter = 'product' | 'carton' | 'both';
type RemoteDcControlsMode = 'none' | 'selected' | 'all';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function readStoredNumber(key: string, fallback: number, min: number, max: number): number {
  const stored = safeLocalStorageGetItem(key);
  if (!stored) return fallback;
  const parsed = Number(stored);
  if (!Number.isFinite(parsed)) return fallback;
  return clamp(parsed, min, max);
}

function readStoredBoolean(key: string, fallback: boolean): boolean {
  const stored = safeLocalStorageGetItem(key);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return fallback;
}

function readStoredControlsMode(): RemoteDcControlsMode {
  const stored = safeLocalStorageGetItem(STORAGE_KEYS.viewControlsMode);
  if (stored === 'none' || stored === 'selected' || stored === 'all') return stored;
  const legacyCollapsed = safeLocalStorageGetItem(
    'forge-master-data-collection-view-controls-collapsed'
  );
  return legacyCollapsed === 'true' ? 'none' : 'selected';
}

function getViewportHeight(): number {
  return typeof window === 'undefined' ? 900 : window.innerHeight;
}

function useViewportHeight(): number {
  const [viewportHeight, setViewportHeight] = useState(getViewportHeight);

  useEffect(() => {
    const handleResize = () => setViewportHeight(getViewportHeight());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewportHeight;
}

function getImageAspectRatio(image?: HTMLImageElement): number {
  const width = image?.naturalWidth || image?.width || 0;
  const height = image?.naturalHeight || image?.height || 0;
  if (width <= 0 || height <= 0) return REMOTE_DC_FALLBACK_CAMERA_ASPECT_RATIO;
  return width / height;
}

export function getRemoteDcNewDesignBarcodes(upc: MasterUPCData): MasterBarcodeEntry[] {
  const byBarcode = new Map<string, MasterBarcodeEntry>();
  const addEntry = (entry: MasterBarcodeEntry | null | undefined) => {
    if (!entry) return;
    const normalizedBarcode = entry?.upc?.trim();
    if (!normalizedBarcode || byBarcode.has(normalizedBarcode)) return;
    byBarcode.set(normalizedBarcode, entry);
  };
  const addBarcode = (barcode: string | null | undefined) => {
    const normalizedBarcode = barcode?.trim();
    if (!normalizedBarcode || byBarcode.has(normalizedBarcode)) return;
    byBarcode.set(normalizedBarcode, {
      upc: normalizedBarcode,
      provenance: ['remote-data-collection'],
      storeDetails: [],
    });
  };

  upc.barcodeSummary?.primaryUpcs.forEach(addEntry);
  upc.barcodeSummary?.alternateUpcs.forEach(addEntry);
  upc.barcodeSummary?.casePacks.forEach(addEntry);
  addBarcode(upc.normalizedUpc);
  addBarcode(upc.lookupNormalizedBarcode);
  upc.designs.forEach((design) => addBarcode(design.normalizedBarcode));
  [
    ...upc.designs.flatMap((design) => design.crops),
    ...(upc.pendingCrops ?? []),
    ...upc.unassignedCrops,
    ...upc.cartonCrops,
    ...(upc.inactiveCrops ?? []),
  ].forEach((crop) => {
    addBarcode(crop.normalizedBarcode);
    addBarcode(crop.normalizedUpc);
  });

  return [...byBarcode.values()];
}

function getSelectedCrops(upc: MasterUPCData, selectedCropIds: Set<string>): MasterCropData[] {
  if (selectedCropIds.size === 0) return [];
  return getAllCropsFromUpc(upc).filter((crop) => selectedCropIds.has(crop.id));
}

export function remoteDcCanCreateNewDesignDirectly(selectedCrops: MasterCropData[]): boolean {
  return Boolean(resolveDraftDesignVerifiedBarcode(selectedCrops));
}

function getAllCropsFromUpc(upc: MasterUPCData): MasterCropData[] {
  return [
    ...upc.designs.flatMap((design) => design.crops),
    ...(upc.pendingCrops ?? []),
    ...upc.unassignedCrops,
    ...upc.cartonCrops,
    ...(upc.inactiveCrops ?? []),
  ];
}

function getBarcodeEntryLabel(entry: MasterBarcodeEntry): string | null {
  const firstNamedStoreDetail = entry.storeDetails.find((detail) => detail.name);
  return firstNamedStoreDetail?.name ?? entry.provenance[0] ?? null;
}

const RemoteDcNewDesignBarcodeDialog: React.FC<{
  itemNumber: string;
  selectedCropCount: number;
  availableBarcodes: MasterBarcodeEntry[];
  onConfirm: (normalizedBarcode: string) => void;
  onClose: () => void;
}> = ({ itemNumber, selectedCropCount, availableBarcodes, onConfirm, onClose }) => {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div
      className="mdc-modal-backdrop"
      data-testid="remote-dc-new-design-barcode-dialog"
      onClick={onClose}
    >
      <div className="mdc-modal mdc-new-design-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="mdc-modal-header">
          <div>
            <h2>Create new design</h2>
            <p>
              Item {itemNumber} · {selectedCropCount} crop
              {selectedCropCount === 1 ? '' : 's'}. Pick the barcode for this design.
            </p>
          </div>
          <button type="button" className="mdc-modal-close" aria-label="Close" onClick={onClose}>
            <CloseOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="mdc-new-design-barcode-list">
          {availableBarcodes.map((entry) => {
            const normalizedBarcode = entry.upc.trim();
            if (!normalizedBarcode) return null;
            const isSelected = selected === normalizedBarcode;
            const label = getBarcodeEntryLabel(entry);
            return (
              <button
                key={normalizedBarcode}
                type="button"
                data-testid={`remote-dc-new-design-barcode-${normalizedBarcode}`}
                className={`mdc-new-design-barcode-option${isSelected ? ' is-selected' : ''}`}
                onClick={() => setSelected(normalizedBarcode)}
              >
                <span>{normalizedBarcode}</span>
                {label ? <small>{label}</small> : null}
              </button>
            );
          })}
        </div>
        <div className="mdc-modal-actions">
          <button type="button" className="mdc-assignment-secondary-action" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="mdc-assignment-secondary-action mdc-assignment-secondary-action--primary"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected)}
          >
            Create design
          </button>
        </div>
      </div>
    </div>
  );
};

function formatPercent(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return `${Math.round(value * 1000) / 10}%`;
}

function masterLabelUrl(
  chainId: number,
  itemNumber?: string | null,
  normalizedBarcode?: string | null,
  designId?: string | null,
  cropIds?: string[]
): string {
  const params = new URLSearchParams({ chain: String(chainId) });
  if (designId) params.set('d', designId);
  else if (itemNumber) params.set('in', itemNumber);
  else if (normalizedBarcode) params.set('nb', normalizedBarcode);
  cropIds?.forEach((cropId) => params.append('c', cropId));
  return `${FORGE_MASTER_LABEL_PATH}?${params.toString()}`;
}

export function masterLabelMultiUrl(
  chainId: number,
  options: {
    itemNumbers?: string[];
    normalizedBarcodes?: string[];
    cropIds?: string[];
    highlightMode?: 'similar' | 'different' | 'bad_quality';
  }
): string {
  const params = new URLSearchParams({ chain: String(chainId) });
  [...new Set(options.itemNumbers ?? [])].forEach((itemNumber) => params.append('in', itemNumber));
  [...new Set(options.normalizedBarcodes ?? [])].forEach((barcode) => params.append('nb', barcode));
  const cropIds = [...new Set(options.cropIds ?? [])];
  cropIds.forEach((cropId) => params.append('c', cropId));
  if (options.highlightMode && cropIds.length > 0) params.set('m', options.highlightMode);
  return `${FORGE_MASTER_LABEL_PATH}?${params.toString()}`;
}

function currentControlOrigin(): string {
  if (typeof window === 'undefined') return 'https://control.focal.systems';
  const { origin } = window.location;
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) return origin;
  if (origin.includes('staging')) return 'https://control.staging.focal.dev';
  return 'https://control.focal.systems';
}

function controlImageUrl(
  storeName?: string | null,
  deviceName?: string | null,
  imageId?: number | null
): string | null {
  if (!storeName || !deviceName || !imageId) return null;
  return `${currentControlOrigin()}/stores/${encodeURIComponent(storeName)}/${encodeURIComponent(
    deviceName
  )}/?image_id=${encodeURIComponent(String(imageId))}`;
}

export function detectionMatchesThresholds(
  detection: RemoteDataCollectionDetection,
  similarityThreshold: number,
  deltaThreshold: number,
  useSimilarityThreshold: boolean,
  useDeltaThreshold: boolean
): boolean {
  const lowSimilarity = detection.similarity < similarityThreshold;
  const lowDelta =
    detection.delta !== undefined && detection.delta !== null && detection.delta < deltaThreshold;
  return (useSimilarityThreshold && lowSimilarity) || (useDeltaThreshold && lowDelta);
}

function hasBarcodeOverlap(left: string[], right: string[]): boolean {
  if (left.length === 0 || right.length === 0) return false;
  const leftSet = new Set(left);
  return right.some((barcode) => leftSet.has(barcode));
}

function isTopCandidateNonFront(group: DetectionCandidateGroup): boolean {
  return group.candidates[0]?.orientation === 'not-front';
}

function areLinkedCandidateGroups(
  sourceGroup: DetectionCandidateGroup,
  candidateGroup: DetectionCandidateGroup
): boolean {
  if (
    sourceGroup.normalizedBarcodes.length === 0 ||
    candidateGroup.normalizedBarcodes.length === 0
  ) {
    return false;
  }
  const sourceLinks = sourceGroup.linkedNormalizedBarcodes;
  const candidateLinks = candidateGroup.linkedNormalizedBarcodes;
  const hasStrongLink =
    hasBarcodeOverlap(candidateGroup.normalizedBarcodes, sourceLinks?.full ?? []) ||
    hasBarcodeOverlap(candidateGroup.normalizedBarcodes, sourceLinks?.partial ?? []) ||
    hasBarcodeOverlap(sourceGroup.normalizedBarcodes, candidateLinks?.full ?? []) ||
    hasBarcodeOverlap(sourceGroup.normalizedBarcodes, candidateLinks?.partial ?? []);
  if (hasStrongLink) return true;
  if (!isTopCandidateNonFront(sourceGroup) && !isTopCandidateNonFront(candidateGroup)) {
    return false;
  }
  return (
    hasBarcodeOverlap(candidateGroup.normalizedBarcodes, sourceLinks?.nonFront ?? []) ||
    hasBarcodeOverlap(sourceGroup.normalizedBarcodes, candidateLinks?.nonFront ?? [])
  );
}

function secondCandidateGroup(
  candidateGroups: DetectionCandidateGroup[],
  topGroup: DetectionCandidateGroup,
  mergeLinkedCandidates: boolean
): DetectionCandidateGroup | null {
  for (const group of candidateGroups.slice(1)) {
    if (group.key === topGroup.key) continue;
    if (mergeLinkedCandidates && areLinkedCandidateGroups(topGroup, group)) continue;
    return group;
  }
  return null;
}

export function rescoreDetectionForMergeLinkedCandidates(
  detection: RemoteDataCollectionDetection,
  mergeLinkedCandidates: boolean,
  similarityThreshold: number,
  deltaThreshold: number
): RemoteDataCollectionDetection {
  const topGroup = detection.candidateGroups[0];
  if (!topGroup) return detection;
  const secondGroup = secondCandidateGroup(
    detection.candidateGroups,
    topGroup,
    mergeLinkedCandidates
  );
  const delta = secondGroup ? topGroup.score - secondGroup.score : null;
  const isLowSimilarity = topGroup.score < similarityThreshold;
  const isLowDelta = delta !== null && delta < deltaThreshold;
  return {
    ...detection,
    topKeyKind: topGroup.keyKind,
    topKey: topGroup.key,
    itemNumber: topGroup.itemNumber,
    normalizedBarcode: topGroup.normalizedBarcodes[0] ?? null,
    normalizedBarcodes: topGroup.normalizedBarcodes,
    similarity: topGroup.score,
    delta,
    secondKey: secondGroup?.key ?? null,
    isLowSimilarity,
    isLowDelta,
    isFlagged: isLowSimilarity || isLowDelta,
  };
}

export function detectionPassesSsmConfidenceThreshold(
  detection: RemoteDataCollectionDetection,
  threshold: number
): boolean {
  return detection.ssmConfidence === undefined || detection.ssmConfidence === null
    ? true
    : detection.ssmConfidence >= threshold;
}

export function orderDetectionsForOverlay(
  detections: RemoteDataCollectionDetection[],
  visibleDetectionType: DetectionTypeFilter
): RemoteDataCollectionDetection[] {
  if (visibleDetectionType !== 'both') return detections;
  return [...detections].sort((left, right) => {
    if (left.classificationType === right.classificationType) return 0;
    return left.classificationType === 'carton' ? -1 : 1;
  });
}

function detectionColor(
  detection: RemoteDataCollectionDetection,
  similarityThreshold: number,
  deltaThreshold: number,
  useSimilarityThreshold: boolean,
  useDeltaThreshold: boolean,
  isSelected: boolean,
  isCollected: boolean
): string {
  if (isSelected) return '#f59e0b';
  if (isCollected) return '#2ea043';
  if (
    !detectionMatchesThresholds(
      detection,
      similarityThreshold,
      deltaThreshold,
      useSimilarityThreshold,
      useDeltaThreshold
    )
  ) {
    return 'rgba(148, 163, 184, 0.9)';
  }
  return detection.classificationType === 'carton'
    ? REMOTE_DC_CARTON_COLOR
    : REMOTE_DC_PRODUCT_COLOR;
}

function detectionCollectionKey(detection: RemoteDataCollectionDetection): string {
  return `${detection.imageId ?? detection.inferenceId}:${detection.shapeList}:${detection.index}`;
}

function toImageBox(
  detection: RemoteDataCollectionDetection,
  similarityThreshold: number,
  deltaThreshold: number,
  useSimilarityThreshold: boolean,
  useDeltaThreshold: boolean,
  selectedDetectionIds: Set<string>,
  collectedDetectionKeys: Set<string>,
  polygonStrokeWidth: number,
  overlayOpacity: number
): ImageBox {
  const [x1, y1, x2, y2] = detection.bbox;
  const isSelected = selectedDetectionIds.has(detection.detectionId);
  const isCollected = collectedDetectionKeys.has(detectionCollectionKey(detection));
  const isHighlighted = detectionMatchesThresholds(
    detection,
    similarityThreshold,
    deltaThreshold,
    useSimilarityThreshold,
    useDeltaThreshold
  );
  return {
    shape: [x1, y1, x2 - x1, y2 - y1],
    item: detection as any,
    color: detectionColor(
      detection,
      similarityThreshold,
      deltaThreshold,
      useSimilarityThreshold,
      useDeltaThreshold,
      isSelected,
      isCollected
    ),
    strokeWidth: isSelected ? polygonStrokeWidth + 3 : polygonStrokeWidth,
    opacity:
      isSelected || isCollected
        ? Math.min(0.5, overlayOpacity + 0.08)
        : isHighlighted
          ? overlayOpacity
          : overlayOpacity * 0.25,
    isPolygon: detection.points.length > 0,
    points: detection.points,
  };
}

function RemoteDataCollectionLoadingCard({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`mdc-loading-card${compact ? ' mdc-loading-card--compact' : ' forge-card'}`}>
      <div className="mdc-loading-topline">
        <span className="mdc-loading-spinner" aria-hidden="true" />
        <div>
          <div className="mdc-loading-title">
            {compact ? 'Loading image context' : 'Preparing Remote Data Collection'}
          </div>
          <div className="mdc-loading-copy">
            {compact
              ? 'Fetching camera and planogram imagery.'
              : 'Loading inference context, planogram imagery, and candidate scoring.'}
          </div>
        </div>
      </div>
      <div className="mdc-loading-skeleton-grid" aria-hidden="true">
        <span className="mdc-loading-skeleton mdc-loading-skeleton--wide" />
        <span className="mdc-loading-skeleton" />
        <span className="mdc-loading-skeleton mdc-loading-skeleton--short" />
      </div>
    </div>
  );
}

function formatDailyNavigationTime(createdAtUtc?: string | null): string {
  if (!createdAtUtc) return '';
  const parsed = new Date(createdAtUtc);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

type DetailModalItem =
  | { kind: 'detection'; detection: RemoteDataCollectionDetection }
  | { kind: 'pog'; bbox: PogBBox; pog?: CameraPog };

type DetectionCandidateGroup = RemoteDataCollectionDetection['candidateGroups'][number];

export type AssignmentContext = {
  source: 'pog' | 'candidate';
  itemNumber?: string | null;
  normalizedBarcode?: string | null;
  normalizedBarcodes: string[];
  label: string;
  pog?: PogBBox;
};

export type AssignmentDraft = {
  id: string;
  chainId: number;
  imageId?: number | null;
  inferenceId?: string | null;
  context: AssignmentContext;
  detectionIds: string[];
  detections: RemoteDataCollectionDetection[];
  cropImages: Record<string, string>;
  createdAtMs: number;
};

export function masterLabelDraftNeedsDiscardConfirmation(
  draft: AssignmentDraft,
  hasEditorChanges: boolean
): boolean {
  return hasEditorChanges || draft.detectionIds.length > 0;
}

export function toggleDetectionInActiveAssignmentDraft(
  drafts: AssignmentDraft[],
  draftId: string,
  detection: RemoteDataCollectionDetection,
  cropImage: string
): AssignmentDraft[] {
  return drafts.map((draft) => {
    if (draft.id !== draftId) return draft;
    const isAlreadyLoaded = draft.detectionIds.includes(detection.detectionId);
    if (isAlreadyLoaded) {
      const nextCropImages = { ...draft.cropImages };
      delete nextCropImages[detection.detectionId];
      return {
        ...draft,
        detectionIds: draft.detectionIds.filter(
          (detectionId) => detectionId !== detection.detectionId
        ),
        detections: draft.detections.filter(
          (draftDetection) => draftDetection.detectionId !== detection.detectionId
        ),
        cropImages: nextCropImages,
      };
    }
    return {
      ...draft,
      detectionIds: [...draft.detectionIds, detection.detectionId],
      detections: [...draft.detections, detection],
      cropImages: {
        ...draft.cropImages,
        [detection.detectionId]: cropImage,
      },
    };
  });
}

export function removeDetectionsFromAssignmentDraft(
  drafts: AssignmentDraft[],
  draftId: string,
  detectionIds: Set<string>
): AssignmentDraft[] {
  return drafts.map((draft) => {
    if (draft.id !== draftId) return draft;
    const nextCropImages = { ...draft.cropImages };
    detectionIds.forEach((detectionId) => {
      delete nextCropImages[detectionId];
    });
    return {
      ...draft,
      detectionIds: draft.detectionIds.filter((detectionId) => !detectionIds.has(detectionId)),
      detections: draft.detections.filter((detection) => !detectionIds.has(detection.detectionId)),
      cropImages: nextCropImages,
    };
  });
}

type RemoteDcPaneMode = 'pog' | 'master-label';

type SegmentedControlOption<TValue extends string> = {
  value: TValue;
  label: string;
  disabled?: boolean;
  badge?: React.ReactNode;
};

function SegmentedControl<TValue extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
}: {
  value: TValue;
  options: SegmentedControlOption<TValue>[];
  onChange: (value: TValue) => void;
  ariaLabel: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={['mdc-stage-tabs', className].filter(Boolean).join(' ')} aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? 'is-active' : ''}
          disabled={option.disabled}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.badge}
        </button>
      ))}
    </div>
  );
}

export type PreparedCropPlacement = {
  detectionId: string;
  normalizedBarcode: string;
  itemNumber?: string | null;
  designId?: string | null;
  orientation?: 'front' | 'not-front';
  classificationType: 'product' | 'carton';
  createDesignGroupId?: string;
};

type RemoteDCMasterLabelSavePayload = {
  state: MasterLabelStateSaveRequest;
  remoteCrops: MasterDataCollectionRemoteCropMaterialization[];
  submittedDetectionIds: string[];
};

type RemoteDCMutationOptions = {
  preserveSelection?: boolean;
};

export function remoteDcShortcutMutationOptions(
  event: Pick<KeyboardEvent, 'shiftKey'>
): RemoteDCMutationOptions {
  return { preserveSelection: event.shiftKey };
}

function valueOrDash(value?: string | number | null): string {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

function bboxCropFromDetection(detection: RemoteDataCollectionDetection): Crop {
  if (detection.points.length > 0) {
    const xs = detection.points.map((point) => point.x);
    const ys = detection.points.map((point) => point.y);
    const x1 = Math.min(...xs);
    const y1 = Math.min(...ys);
    const x2 = Math.max(...xs);
    const y2 = Math.max(...ys);
    return [x1, y1, Math.max(1, x2 - x1), Math.max(1, y2 - y1)];
  }
  const [x1 = 0, y1 = 0, x2 = x1 + 1, y2 = y1 + 1] = detection.bbox;
  return [x1, y1, Math.max(1, x2 - x1), Math.max(1, y2 - y1)];
}

export function squareCropDrawGeometry(
  crop: Crop,
  sourceSize: { width: number; height: number },
  targetSize: number
): {
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
  drawX: number;
  drawY: number;
  drawWidth: number;
  drawHeight: number;
} {
  const sourceX = clamp(Math.floor(crop[0]), 0, Math.max(0, sourceSize.width - 1));
  const sourceY = clamp(Math.floor(crop[1]), 0, Math.max(0, sourceSize.height - 1));
  const maxWidth = Math.max(1, sourceSize.width - sourceX);
  const maxHeight = Math.max(1, sourceSize.height - sourceY);
  const sourceWidth = clamp(Math.ceil(crop[2]), 1, maxWidth);
  const sourceHeight = clamp(Math.ceil(crop[3]), 1, maxHeight);
  const scale = Math.min(targetSize / sourceWidth, targetSize / sourceHeight);
  const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
  const drawHeight = Math.max(1, Math.round(sourceHeight * scale));

  return {
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    drawX: Math.floor((targetSize - drawWidth) / 2),
    drawY: Math.floor((targetSize - drawHeight) / 2),
    drawWidth,
    drawHeight,
  };
}

function bboxCropFromPog(bbox: PogBBox): Crop {
  const x = Math.min(bbox.x1, bbox.x2);
  const y = Math.min(bbox.y1, bbox.y2);
  return [x, y, Math.max(1, Math.abs(bbox.x2 - bbox.x1)), Math.max(1, Math.abs(bbox.y2 - bbox.y1))];
}

type RgbaPixel = readonly [number, number, number, number];

function colorDistance(left: RgbaPixel, right: RgbaPixel): number {
  const red = left[0] - right[0];
  const green = left[1] - right[1];
  const blue = left[2] - right[2];
  return Math.sqrt(red * red + green * green + blue * blue);
}

function averagePixels(pixels: RgbaPixel[]): RgbaPixel {
  if (pixels.length === 0) return [255, 255, 255, 255];
  const sum = pixels.reduce(
    (current, pixel) => [
      current[0] + pixel[0],
      current[1] + pixel[1],
      current[2] + pixel[2],
      current[3] + pixel[3],
    ],
    [0, 0, 0, 0]
  );
  return [
    sum[0] / pixels.length,
    sum[1] / pixels.length,
    sum[2] / pixels.length,
    sum[3] / pixels.length,
  ];
}

function dominantBorderColor(
  x: number,
  y: number,
  width: number,
  height: number,
  getPixel: (x: number, y: number) => RgbaPixel
): RgbaPixel {
  const edgePixels: RgbaPixel[] = [];
  const step = Math.max(1, Math.floor(Math.max(width, height) / 160));
  for (let px = x; px < x + width; px += step) {
    edgePixels.push(getPixel(px, y));
    edgePixels.push(getPixel(px, y + height - 1));
  }
  for (let py = y; py < y + height; py += step) {
    edgePixels.push(getPixel(x, py));
    edgePixels.push(getPixel(x + width - 1, py));
  }

  const buckets = new Map<string, RgbaPixel[]>();
  edgePixels.forEach((pixel) => {
    if (pixel[3] < 20) return;
    const key = [0, 1, 2].map((channel) => Math.round(pixel[channel] / 18)).join(':');
    buckets.set(key, [...(buckets.get(key) ?? []), pixel]);
  });

  let dominant: RgbaPixel[] = [];
  buckets.forEach((pixels) => {
    if (pixels.length > dominant.length) dominant = pixels;
  });
  return averagePixels(dominant.length > 0 ? dominant : edgePixels);
}

export function trimPogCropToVisibleContent(
  crop: Crop,
  imageSize: { width: number; height: number },
  getPixel: (x: number, y: number) => RgbaPixel
): Crop {
  const [cropX, cropY, cropWidth, cropHeight] = crop.map(Math.round) as Crop;
  const x = clamp(cropX, 0, Math.max(0, imageSize.width - 1));
  const y = clamp(cropY, 0, Math.max(0, imageSize.height - 1));
  const width = clamp(cropWidth, 1, Math.max(1, imageSize.width - x));
  const height = clamp(cropHeight, 1, Math.max(1, imageSize.height - y));
  const background = dominantBorderColor(x, y, width, height, getPixel);
  const step = Math.max(1, Math.floor(Math.max(width, height) / 260));
  const threshold = 34;
  let minX = x + width;
  let minY = y + height;
  let maxX = x;
  let maxY = y;
  let contentPixels = 0;

  for (let py = y; py < y + height; py += step) {
    for (let px = x; px < x + width; px += step) {
      const pixel = getPixel(px, py);
      if (pixel[3] < 20 || colorDistance(pixel, background) < threshold) continue;
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
      contentPixels++;
    }
  }

  if (contentPixels < 12 || minX > maxX || minY > maxY) return [x, y, width, height];

  const padding = Math.max(8, Math.round(Math.min(width, height) * 0.04));
  const nextX = clamp(minX - padding, x, x + width - 1);
  const nextY = clamp(minY - padding, y, y + height - 1);
  const nextRight = clamp(maxX + padding, nextX + 1, x + width);
  const nextBottom = clamp(maxY + padding, nextY + 1, y + height);
  const nextWidth = nextRight - nextX;
  const nextHeight = nextBottom - nextY;

  const widthReduction = 1 - nextWidth / width;
  const heightReduction = 1 - nextHeight / height;
  if (widthReduction < 0.04 && heightReduction < 0.04) return [x, y, width, height];

  return [nextX, nextY, nextWidth, nextHeight];
}

function trimPogCropUsingImage(image: HTMLImageElement, crop: Crop): Crop | null {
  const [x, y, width, height] = crop.map(Math.round) as Crop;
  if (!image.naturalWidth || !image.naturalHeight || width <= 1 || height <= 1) return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, x, y, width, height, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const localCrop = trimPogCropToVisibleContent(
      [0, 0, width, height],
      { width, height },
      (px, py) => {
        const offset = (py * width + px) * 4;
        return [
          imageData.data[offset],
          imageData.data[offset + 1],
          imageData.data[offset + 2],
          imageData.data[offset + 3],
        ];
      }
    );
    return [x + localCrop[0], y + localCrop[1], localCrop[2], localCrop[3]];
  } catch {
    return null;
  }
}

function detailDetectionBox(detection: RemoteDataCollectionDetection): ImageBox {
  return {
    shape: bboxCropFromDetection(detection),
    item: detection as any,
    color:
      detection.classificationType === 'carton' ? REMOTE_DC_CARTON_COLOR : REMOTE_DC_PRODUCT_COLOR,
    strokeWidth: 5,
    opacity: 0.14,
    isPolygon: detection.points.length > 0,
    points: detection.points,
  };
}

function detailPogBox(bbox: PogBBox): ImageBox {
  return {
    shape: bboxCropFromPog(bbox),
    item: bbox as any,
    color: '#2ea043',
    strokeWidth: 5,
    opacity: 0.14,
    shelfColumnLabel: bbox.shelfColumnLabel,
    embeddingCropCount: bbox.embeddingCropCount ?? bbox.embedding_crop_count,
  };
}

function PogDetailRow({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}): JSX.Element {
  return (
    <div className="mdc-pog-detail-row">
      <span>{label}</span>
      <strong>{valueOrDash(value)}</strong>
    </div>
  );
}

function PogImageDetailPreview({
  bbox,
  imageElement,
  imageBase64,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  bbox: PogBBox;
  imageElement?: HTMLImageElement;
  imageBase64?: string;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | undefined>(undefined);
  const [productImageFailed, setProductImageFailed] = useState(false);

  useEffect(() => {
    if (imageElement || !imageBase64) {
      setLoadedImage(undefined);
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setLoadedImage(image);
    };
    image.src = imageBase64;
    return () => {
      cancelled = true;
    };
  }, [imageBase64, imageElement]);

  const previewImage = imageElement || loadedImage;
  const zoomUrl = imageBase64 || previewImage?.src || null;
  const itemNumber = bbox.itemNumber ? String(bbox.itemNumber) : undefined;
  const upc = bbox.upc ? String(bbox.upc) : undefined;
  const productImageUrl = bbox.imageUri || bbox.productImageUri || null;
  const rawPogCrop = useMemo(() => bboxCropFromPog(bbox), [bbox]);
  const [trimmedPogCrop, setTrimmedPogCrop] = useState<Crop | null>(null);
  const productZoomMeta = {
    id: itemNumber || upc || String(bbox.ilItemId),
    storeId: bbox.storeId ? String(bbox.storeId) : undefined,
    normalizedBarcode: upc,
  };

  useEffect(() => {
    setProductImageFailed(false);
  }, [productImageUrl]);

  useEffect(() => {
    if (!previewImage) {
      setTrimmedPogCrop(null);
      return;
    }
    setTrimmedPogCrop(trimPogCropUsingImage(previewImage, rawPogCrop));
  }, [previewImage, rawPogCrop]);

  if (productImageUrl && !productImageFailed) {
    return (
      <div
        onMouseEnter={
          onImageHover
            ? (event) => onImageHover(event, productImageUrl, productZoomMeta)
            : undefined
        }
        onMouseMove={
          onImageMove ? (event) => onImageMove(event, productImageUrl, productZoomMeta) : undefined
        }
        onMouseLeave={onImageLeave}
      >
        <img
          src={productImageUrl}
          alt={bbox.name || 'POG product'}
          className="mdc-pog-product-cut"
          onError={() => setProductImageFailed(true)}
        />
      </div>
    );
  }

  if (!previewImage) {
    return (
      <div className="mdc-detail-preview-empty">
        {imageBase64 ? 'Loading preview...' : 'Preview unavailable'}
      </div>
    );
  }

  const previewCrop = trimmedPogCrop ?? rawPogCrop;
  const previewAspectRatio = previewCrop[2] / previewCrop[3];
  const previewMaxWidth = Math.max(1, Math.round(previewAspectRatio * 320));

  return (
    <div
      onMouseEnter={
        zoomUrl && onImageHover
          ? (event) =>
              onImageHover(event, zoomUrl, {
                id: itemNumber || upc || String(bbox.ilItemId),
                storeId: bbox.storeId ? String(bbox.storeId) : undefined,
                normalizedBarcode: upc,
              })
          : undefined
      }
      onMouseMove={
        zoomUrl && onImageMove
          ? (event) =>
              onImageMove(event, zoomUrl, {
                id: itemNumber || upc || String(bbox.ilItemId),
                storeId: bbox.storeId ? String(bbox.storeId) : undefined,
                normalizedBarcode: upc,
              })
          : undefined
      }
      onMouseLeave={zoomUrl ? onImageLeave : undefined}
    >
      <BoxedImage
        image={previewImage}
        crop={previewCrop}
        cropPadding={0}
        boxes={[{ ...detailPogBox(bbox), shape: previewCrop }]}
        defaultFillOpacity={0.12}
        styles={{
          width: '100%',
          maxWidth: previewMaxWidth,
          aspectRatio: `${previewCrop[2]} / ${previewCrop[3]}`,
          display: 'block',
          margin: '0 auto',
          maxHeight: 320,
          background: 'var(--elevated)',
          borderRadius: 8,
        }}
      />
    </div>
  );
}

function MasterDataCollectionDetailsModal({
  item,
  storeName,
  cameraImageElement,
  cameraImageBase64,
  chainId,
  selectedDetectionCount,
  isAssigningCandidate,
  onAssignCandidate,
  onImageHover,
  onImageMove,
  onImageLeave,
  onClose,
}: {
  item: DetailModalItem;
  storeName?: string | null;
  cameraImageElement?: HTMLImageElement;
  cameraImageBase64?: string;
  chainId?: number;
  selectedDetectionCount: number;
  isAssigningCandidate: boolean;
  onAssignCandidate: (detection: RemoteDataCollectionDetection, target: AssignmentContext) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
  onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose]);

  const isDetection = item.kind === 'detection';
  const title = isDetection ? 'Detection details' : 'POG product details';
  const allShownForgeUrl = useMemo(
    () => (isDetection ? allShownForgeUrlForDetection(item.detection, chainId) : null),
    [chainId, isDetection, item]
  );

  return (
    <aside className="mdc-inspector-panel" role="dialog" aria-modal="false" aria-label={title}>
      <header className="mdc-modal-header">
        <div>
          <h2>{title}</h2>
        </div>
        <div className="mdc-modal-header-actions">
          {allShownForgeUrl ? (
            <MasterLabelLink to={allShownForgeUrl} className="mdc-modal-link">
              Open in Forge <OpenInNewOutlinedIcon style={{ fontSize: 14 }} />
            </MasterLabelLink>
          ) : null}
          <button
            type="button"
            className="mdc-modal-close"
            aria-label="Close details"
            onClick={onClose}
          >
            <CloseOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </header>
      {isDetection ? (
        <DetectionDetailsContent
          detection={item.detection}
          storeName={storeName}
          cameraImageElement={cameraImageElement}
          cameraImageBase64={cameraImageBase64}
          chainId={chainId}
          selectedDetectionCount={selectedDetectionCount}
          isAssigningCandidate={isAssigningCandidate}
          onAssignCandidate={onAssignCandidate}
          onImageHover={onImageHover}
          onImageMove={onImageMove}
          onImageLeave={onImageLeave}
        />
      ) : (
        <PogDetailsContent bbox={item.bbox} pog={item.pog} chainId={chainId} />
      )}
    </aside>
  );
}

function allShownForgeUrlForDetection(
  detection: RemoteDataCollectionDetection,
  chainId?: number
): string | null {
  if (!chainId || detection.candidateGroups.length === 0) return null;
  const cropIds = detection.candidateGroups.slice(0, 3).flatMap((group) =>
    (group.referenceCrops ?? [])
      .slice(0, 3)
      .map((crop) => crop.cropId)
      .filter(Boolean)
  );
  if (cropIds.length === 0) return null;
  return masterLabelMultiUrl(chainId, {
    cropIds,
    highlightMode: 'similar',
  });
}

function DetailCropPreview({
  imageElement,
  imageBase64,
  crop,
  boxes,
  compact = false,
  zoomImageUrl,
  zoomMeta,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  imageElement?: HTMLImageElement;
  imageBase64?: string;
  crop: Crop;
  boxes: ImageBox[];
  compact?: boolean;
  zoomImageUrl?: string;
  zoomMeta?: ZoomMeta;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | undefined>(undefined);

  useEffect(() => {
    if (imageElement || !imageBase64) {
      setLoadedImage(undefined);
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setLoadedImage(image);
    };
    image.src = imageBase64;
    return () => {
      cancelled = true;
    };
  }, [imageBase64, imageElement]);

  const previewImage = imageElement || loadedImage;
  const zoomUrl = useMemo(
    () => zoomImageUrl || (previewImage ? croppedImageDataUrl(previewImage, crop) : null),
    [crop, previewImage, zoomImageUrl]
  );
  if (!previewImage) {
    return (
      <div className="mdc-detail-preview-empty">
        {imageBase64 ? 'Loading preview...' : 'Preview unavailable'}
      </div>
    );
  }

  const previewPadding = compact
    ? clamp(Math.round(Math.min(crop[2], crop[3]) * 0.28), 12, 52)
    : clamp(Math.round(Math.max(crop[2], crop[3]) * 0.32), 72, 320);

  return (
    <div
      onMouseEnter={
        zoomUrl && onImageHover ? (event) => onImageHover(event, zoomUrl, zoomMeta) : undefined
      }
      onMouseMove={
        zoomUrl && onImageMove ? (event) => onImageMove(event, zoomUrl, zoomMeta) : undefined
      }
      onMouseLeave={zoomUrl ? onImageLeave : undefined}
    >
      <BoxedImage
        image={previewImage}
        crop={crop}
        cropPadding={previewPadding}
        boxes={boxes}
        defaultFillOpacity={0.12}
        styles={{
          width: '100%',
          height: compact ? '100%' : undefined,
          maxHeight: compact ? 232 : 320,
          background: 'var(--elevated)',
          borderRadius: 8,
        }}
      />
    </div>
  );
}

function candidateBarcode(
  group: RemoteDataCollectionDetection['candidateGroups'][number]
): string | null {
  return (
    group.normalizedBarcodes[0] ||
    group.candidates.find((candidate) => candidate.normalizedBarcode)?.normalizedBarcode ||
    (group.keyKind === 'barcode' ? group.key : null)
  );
}

function candidateBarcodes(
  group: RemoteDataCollectionDetection['candidateGroups'][number]
): string[] {
  return [
    ...new Set(
      [
        ...group.normalizedBarcodes,
        ...group.candidates
          .map((candidate) => candidate.normalizedBarcode)
          .filter((barcode): barcode is string => Boolean(barcode)),
        group.keyKind === 'barcode' ? group.key : null,
      ].filter((barcode): barcode is string => Boolean(barcode))
    ),
  ];
}

function croppedImageDataUrl(image: HTMLImageElement, crop: Crop): string | null {
  const [x, y, width, height] = crop.map((value) => Math.max(0, Math.round(value))) as Crop;
  if (width <= 0 || height <= 0) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(image, x, y, width, height, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

function candidateAssignmentTarget(
  group: DetectionCandidateGroup,
  item?: ApiMasterLabelUPCData | null
): AssignmentContext {
  const normalizedBarcode =
    item?.normalizedUpc ||
    item?.upc ||
    candidateBarcode(group) ||
    group.normalizedBarcodes[0] ||
    null;
  const itemNumber = item?.itemNumber || group.itemNumber || null;
  return {
    source: 'candidate',
    itemNumber,
    normalizedBarcode,
    normalizedBarcodes: candidateBarcodes(group),
    label: item?.productName || group.productName || itemNumber || normalizedBarcode || group.key,
  };
}

function SummaryRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value?: string | number | null;
  mono?: boolean;
}) {
  return (
    <div className="mdc-summary-row">
      <span>{label}</span>
      <strong className={mono ? 'mdc-summary-value--mono' : undefined}>{valueOrDash(value)}</strong>
    </div>
  );
}

function DetectionSummaryPanel({ detection }: { detection: RemoteDataCollectionDetection }) {
  return (
    <aside className="mdc-detection-summary-card">
      <div className="mdc-summary-section">
        <div className="mdc-summary-section-title">Detection</div>
        <SummaryRow label="Type" value={detection.classificationType} />
        <SummaryRow label="Similarity" value={formatPercent(detection.similarity)} />
        <SummaryRow label="Delta" value={formatPercent(detection.delta)} />
        <SummaryRow label="SSM confidence" value={formatPercent(detection.ssmConfidence)} />
        <SummaryRow label="Store" value={detection.storeName || detection.storeId} />
      </div>
      <div className="mdc-summary-section">
        <div className="mdc-summary-section-title">Shelf position</div>
        <div className="mdc-summary-mini-grid">
          <SummaryRow label="Shelf" value={detection.shelf} />
          <SummaryRow label="Mod" value={detection.mod} />
          <SummaryRow label="Column" value={detection.column} />
        </div>
      </div>
    </aside>
  );
}

function assignmentCacheKey(
  chainId: number | undefined,
  context: AssignmentContext | null
): string | null {
  if (!chainId || !context) return null;
  if (context.itemNumber) return `${chainId}:item:${context.itemNumber}`;
  if (context.normalizedBarcode) return `${chainId}:barcode:${context.normalizedBarcode}`;
  return null;
}

function assignmentContextIdentity(context: AssignmentContext): string {
  if (context.itemNumber) return `item:${context.itemNumber}`;
  if (context.normalizedBarcode) return `barcode:${context.normalizedBarcode}`;
  const firstBarcode = context.normalizedBarcodes[0];
  if (firstBarcode) return `barcode:${firstBarcode}`;
  return `${context.source}:${context.label}`;
}

function assignmentDraftId(context: AssignmentContext, imageId?: number | null): string {
  return `${assignmentContextIdentity(context)}:${imageId ?? 'current'}`;
}

function pogAssignmentContext(bbox: PogBBox): AssignmentContext {
  const normalizedBarcode = bbox.upc ? String(bbox.upc) : null;
  const itemNumber = bbox.itemNumber ? String(bbox.itemNumber) : null;
  return {
    source: 'pog',
    itemNumber,
    normalizedBarcode,
    normalizedBarcodes: normalizedBarcode ? [normalizedBarcode] : [],
    label: String(bbox.name || itemNumber || normalizedBarcode || 'POG item'),
    pog: bbox,
  };
}

function remoteDcPogImageKey(pog: CameraPog, pogIndex: number): string {
  return `${pog.ilSectionId ?? pogIndex}:${pog.pogImageUri || 'no-image'}`;
}

export function orderRemoteDcPogs(
  pogs: CameraPog[],
  modTagNames: readonly string[],
  ilSectionIds: readonly number[] = []
): CameraPog[] {
  const tagOrder = new Map<string, number>();
  modTagNames.forEach((tagName, index) => {
    if (!tagOrder.has(tagName)) tagOrder.set(tagName, index);
  });
  const sectionOrder = new Map<number, number>();
  ilSectionIds.forEach((ilSectionId, index) => {
    if (!sectionOrder.has(ilSectionId)) sectionOrder.set(ilSectionId, index);
  });

  return [...pogs].sort((left, right) => {
    const leftTagIndex = tagOrder.get(left.tagName);
    const rightTagIndex = tagOrder.get(right.tagName);
    if (leftTagIndex !== undefined || rightTagIndex !== undefined) {
      if (leftTagIndex === undefined) return 1;
      if (rightTagIndex === undefined) return -1;
      if (leftTagIndex !== rightTagIndex) return leftTagIndex - rightTagIndex;
    }

    const leftSectionIndex = sectionOrder.get(left.ilSectionId);
    const rightSectionIndex = sectionOrder.get(right.ilSectionId);
    if (leftSectionIndex !== undefined || rightSectionIndex !== undefined) {
      if (leftSectionIndex === undefined) return 1;
      if (rightSectionIndex === undefined) return -1;
      if (leftSectionIndex !== rightSectionIndex) return leftSectionIndex - rightSectionIndex;
    }

    const tagCompare = left.tagName.localeCompare(right.tagName, 'en', {
      numeric: true,
      sensitivity: 'base',
    });
    if (tagCompare !== 0) return tagCompare;
    if (left.modCoverageId !== right.modCoverageId) return left.modCoverageId - right.modCoverageId;
    return left.ilSectionId - right.ilSectionId;
  });
}

export function RemoteDCPogPane({
  currentPogs,
  preloadedImages,
  preloadedImagesB64,
  selectedIlItemIds,
  hiddenPogImageKeys,
  showFallbackImages,
  showShelfColumnLabels,
  showEmbeddingsCount,
  imageBackgroundColor,
  showImageFrameBorder,
  fallbackPogBoxColor,
  selectedPogBoxColor,
  maxPogImageHeight,
  onPogBoxClick,
  onPogBoxRightClick,
  onTogglePogImage,
}: {
  currentPogs: CameraPog[];
  preloadedImages?: Record<string, HTMLImageElement>;
  preloadedImagesB64: Record<string, string>;
  selectedIlItemIds: Set<number>;
  hiddenPogImageKeys: ReadonlySet<string>;
  showFallbackImages?: boolean;
  showShelfColumnLabels?: boolean;
  showEmbeddingsCount?: boolean;
  imageBackgroundColor?: string;
  showImageFrameBorder?: boolean;
  fallbackPogBoxColor?: string;
  selectedPogBoxColor?: string;
  maxPogImageHeight?: number;
  onPogBoxClick?: (bbox: PogBBox) => void;
  onPogBoxRightClick?: (bbox: PogBBox) => void;
  onTogglePogImage: (pogKey: string) => void;
}): JSX.Element {
  const pogsWithVisibility = currentPogs.map((pog, pogIndex) => ({
    key: remoteDcPogImageKey(pog, pogIndex),
    pog,
    pogIndex,
    isHidden: hiddenPogImageKeys.has(remoteDcPogImageKey(pog, pogIndex)),
  }));
  const visiblePogs = pogsWithVisibility.filter(({ isHidden }) => !isHidden);
  const visiblePogCount = visiblePogs.length;
  const pogGridTemplateColumns =
    visiblePogCount === 4
      ? 'repeat(2, minmax(0, 1fr))'
      : visiblePogCount > 1
        ? 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))'
        : '1fr';

  return (
    <div className="mdc-direct-pane">
      <div className="mdc-direct-pane-header">
        <div>
          <h2>
            POG Images ({visiblePogCount}/{currentPogs.length})
          </h2>
        </div>
        <div className="mdc-pog-visibility-buttons">
          {pogsWithVisibility.map(({ key, pogIndex, isHidden }) => (
            <button
              key={key}
              type="button"
              aria-pressed={!isHidden}
              className={isHidden ? '' : 'is-active'}
              onClick={() => onTogglePogImage(key)}
            >
              {pogIndex + 1}
            </button>
          ))}
        </div>
      </div>
      {visiblePogCount === 0 ? (
        <div className="mdc-assignment-empty">All POG images are hidden.</div>
      ) : (
        <div className="mdc-pog-pane-grid" style={{ gridTemplateColumns: pogGridTemplateColumns }}>
          {visiblePogs.map(({ key, pog, pogIndex }) => {
            const preloadedPogImage = pog?.pogImageUri
              ? preloadedImagesB64[pog.pogImageUri]
              : undefined;
            const preloadedPogImageElement =
              pog?.pogImageUri && preloadedImages ? preloadedImages[pog.pogImageUri] : undefined;
            const pogBboxes = (pog?.bboxes ?? []).map((bbox) => {
              const isSelected = selectedIlItemIds.size > 0 && selectedIlItemIds.has(bbox.ilItemId);
              const color = isSelected
                ? selectedPogBoxColor || '#2ea043'
                : bbox.modelMatched
                  ? '#2196F3'
                  : fallbackPogBoxColor || '#64748b';
              const shelfColumnLabel =
                showShelfColumnLabels && bbox.shelf !== undefined && bbox.column !== undefined
                  ? currentPogs.length > 1
                    ? `${pogIndex + 1}-${bbox.shelf}-${bbox.column}`
                    : `${bbox.shelf}-${bbox.column}`
                  : undefined;
              return {
                ...bbox,
                ilSectionId: bbox.ilSectionId ?? pog.ilSectionId,
                modCoverageId: bbox.modCoverageId ?? pog.modCoverageId,
                pogImageUri: bbox.pogImageUri || pog.pogImageUri,
                color,
                shelfColumnLabel,
                embeddingCropCount: bbox.embeddingCropCount ?? bbox.embedding_crop_count,
              };
            });

            return (
              <div key={key} className="mdc-pog-pane-card">
                <div className="mdc-pog-pane-card-label">POG {pogIndex + 1}</div>
                {pog?.pogImageUri ? (
                  <ImageViewer<PogBBox>
                    image={preloadedPogImageElement}
                    imageBase64={preloadedPogImage || pog.pogImageUri}
                    bboxes={pogBboxes}
                    onBoxClick={
                      onPogBoxClick
                        ? (bbox) =>
                            onPogBoxClick({
                              ...bbox,
                              ilSectionId: bbox.ilSectionId ?? pog.ilSectionId,
                              modCoverageId: bbox.modCoverageId ?? pog.modCoverageId,
                              pogImageUri: bbox.pogImageUri || pog.pogImageUri,
                              previewImageBase64: preloadedPogImage?.startsWith('data:')
                                ? preloadedPogImage
                                : undefined,
                              previewImageElement: preloadedPogImageElement,
                            } as PogBBox)
                        : () => undefined
                    }
                    onBoxRightClick={
                      onPogBoxRightClick
                        ? (box) =>
                            onPogBoxRightClick({
                              ...(box as PogBBox),
                              ilSectionId: (box as PogBBox).ilSectionId ?? pog.ilSectionId,
                              modCoverageId: (box as PogBBox).modCoverageId ?? pog.modCoverageId,
                              pogImageUri: (box as PogBBox).pogImageUri || pog.pogImageUri,
                              previewImageBase64: preloadedPogImage?.startsWith('data:')
                                ? preloadedPogImage
                                : undefined,
                              previewImageElement: preloadedPogImageElement,
                            } as PogBBox)
                        : undefined
                    }
                    boxActions={[]}
                    showShelfColumnLabels={showShelfColumnLabels}
                    showEmbeddingsCount={showEmbeddingsCount}
                    embeddingCountBadgeVariant="compact-dark"
                    showFallbackImages={showFallbackImages}
                    imageBackgroundColor={imageBackgroundColor}
                    showFrameBorder={showImageFrameBorder}
                    height={maxPogImageHeight}
                    maxDisplayHeight={maxPogImageHeight}
                    fitToContainerWidth
                    imageContentVerticalAlign="flex-start"
                  />
                ) : (
                  <div className="mdc-assignment-empty">No POG image available.</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function DetectionTypeVisibilityControls({
  selectedType,
  onSelectType,
}: {
  selectedType: DetectionTypeFilter;
  onSelectType: (type: DetectionTypeFilter) => void;
}): JSX.Element {
  return (
    <SegmentedControl
      value={selectedType}
      options={[
        { value: 'product', label: 'Product' },
        { value: 'carton', label: 'Carton' },
        { value: 'both', label: 'Both' },
      ]}
      onChange={onSelectType}
      ariaLabel="Detection type visibility"
      className="mdc-detection-type-tabs"
    />
  );
}

function remoteTempCropId(detectionId: string): string {
  return `remote-dc:${detectionId}`;
}

function isRemoteTempCropId(cropId: string): boolean {
  return cropId.startsWith('remote-dc:');
}

function remoteDetectionIdFromTempCropId(cropId: string): string | null {
  return isRemoteTempCropId(cropId) ? cropId.slice('remote-dc:'.length) : null;
}

export function deselectRemoteCropsForDetections(
  selectedCropIds: Set<string>,
  detectionIds: Set<string>
): Set<string> {
  const next = new Set(selectedCropIds);
  detectionIds.forEach((detectionId) => {
    next.delete(remoteTempCropId(detectionId));
  });
  return next;
}

export function remoteCropIdsNeedingAutoSelection(
  tempCropIds: string[],
  suppressedAutoSelectCropIds: Set<string>,
  alreadyAutoSelectedCropIds: Set<string> = new Set()
): string[] {
  return tempCropIds.filter(
    (cropId) => !suppressedAutoSelectCropIds.has(cropId) && !alreadyAutoSelectedCropIds.has(cropId)
  );
}

function fallbackMasterUpc(
  context: AssignmentContext,
  item?: ApiMasterLabelUPCData | null
): MasterUPCData {
  const primaryBarcode =
    context.normalizedBarcode ||
    item?.normalizedUpc ||
    item?.upc ||
    context.normalizedBarcodes[0] ||
    context.itemNumber ||
    'unknown';
  const itemNumber = context.itemNumber || item?.itemNumber || primaryBarcode;
  const productName = item?.productName || context.label || itemNumber;
  return {
    columnKey: itemNumber,
    upc: primaryBarcode,
    normalizedUpc: primaryBarcode,
    productName,
    productImageUrl: item?.productImageUrl || undefined,
    itemNumber,
    actualItemNumber: context.itemNumber || item?.itemNumber || null,
    lookupKind: context.itemNumber ? 'item_number' : 'normalized_barcode',
    lookupValue: context.itemNumber || primaryBarcode,
    lookupNormalizedBarcode: primaryBarcode,
    barcodeSummary: {
      primaryUpcs: [
        {
          upc: primaryBarcode,
          provenance: ['remote-data-collection'],
          storeDetails: [
            {
              storeLabel: 'Target',
              role: 'primary',
              name: productName,
              images: [],
              isActive: true,
            },
          ],
        },
      ],
      alternateUpcs: context.normalizedBarcodes
        .filter((barcode) => barcode !== primaryBarcode)
        .map((barcode) => ({
          upc: barcode,
          provenance: ['remote-data-collection'],
          storeDetails: [],
        })),
      casePacks: [],
    },
    designs: [],
    pendingCrops: [],
    unassignedCrops: [],
    cartonCrops: [],
    inactiveCrops: [],
  };
}

export function cropImageFromDetection(
  detection: RemoteDataCollectionDetection,
  cameraImageElement?: HTMLImageElement,
  fallbackImage?: string
): string {
  if (!cameraImageElement) return fallbackImage || '';
  try {
    const crop = bboxCropFromDetection(detection);
    const sourceWidth = cameraImageElement.naturalWidth || cameraImageElement.width;
    const sourceHeight = cameraImageElement.naturalHeight || cameraImageElement.height;
    if (!sourceWidth || !sourceHeight) return fallbackImage || '';
    const geometry = squareCropDrawGeometry(
      crop,
      { width: sourceWidth, height: sourceHeight },
      REMOTE_DC_DRAFT_CROP_PREVIEW_SIZE_PX
    );
    const canvas = document.createElement('canvas');
    canvas.width = REMOTE_DC_DRAFT_CROP_PREVIEW_SIZE_PX;
    canvas.height = REMOTE_DC_DRAFT_CROP_PREVIEW_SIZE_PX;
    const context = canvas.getContext('2d');
    if (!context) return fallbackImage || '';
    context.fillStyle = '#000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(
      cameraImageElement,
      geometry.sourceX,
      geometry.sourceY,
      geometry.sourceWidth,
      geometry.sourceHeight,
      geometry.drawX,
      geometry.drawY,
      geometry.drawWidth,
      geometry.drawHeight
    );
    return canvas.toDataURL('image/jpeg', 0.88);
  } catch {
    return fallbackImage || '';
  }
}

function cropPreviewFromDetection(
  detection: RemoteDataCollectionDetection,
  cameraImageElement?: HTMLImageElement,
  sourceImageUrl?: string
): MasterCropData['cropPreview'] | undefined {
  if (!cameraImageElement || !sourceImageUrl) return undefined;
  const sourceWidth = cameraImageElement.naturalWidth || cameraImageElement.width;
  const sourceHeight = cameraImageElement.naturalHeight || cameraImageElement.height;
  if (!sourceWidth || !sourceHeight) return undefined;

  const crop = bboxCropFromDetection(detection);
  const geometry = squareCropDrawGeometry(crop, { width: sourceWidth, height: sourceHeight }, 1);
  const cropX = geometry.sourceX;
  const cropY = geometry.sourceY;
  const cropWidth = geometry.sourceWidth;
  const cropHeight = geometry.sourceHeight;

  return {
    sourceImageUrl,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    sourceWidth,
    sourceHeight,
  };
}

export function removeSavedDetectionsFromDrafts(
  drafts: AssignmentDraft[],
  draftId: string,
  savedDetectionIds: Set<string>
): AssignmentDraft[] {
  return drafts
    .map((candidateDraft) =>
      candidateDraft.id === draftId
        ? {
            ...candidateDraft,
            detectionIds: candidateDraft.detectionIds.filter(
              (detectionId) => !savedDetectionIds.has(detectionId)
            ),
            detections: candidateDraft.detections.filter(
              (detection) => !savedDetectionIds.has(detection.detectionId)
            ),
            cropImages: Object.fromEntries(
              Object.entries(candidateDraft.cropImages).filter(
                ([detectionId]) => !savedDetectionIds.has(detectionId)
              )
            ),
          }
        : candidateDraft
    )
    .filter(
      (candidateDraft) => candidateDraft.id !== draftId || candidateDraft.detectionIds.length > 0
    );
}

function buildDraftBaseUpc(
  draft: AssignmentDraft,
  item: ApiMasterLabelUPCData | null | undefined
): MasterUPCData {
  const base = item ? transformApiUPCData(item) : fallbackMasterUpc(draft.context, item);
  return base;
}

function buildRemoteTempCrops(
  draft: AssignmentDraft,
  base: MasterUPCData,
  detections: RemoteDataCollectionDetection[],
  cameraImageElement?: HTMLImageElement,
  cameraImageFallbackSrc?: string
): MasterCropData[] {
  const normalizedBarcode =
    draft.context.normalizedBarcode ||
    base.normalizedUpc ||
    base.upc ||
    draft.context.normalizedBarcodes[0] ||
    undefined;
  return detections.map((detection, index) => {
    const cropImage =
      draft.cropImages[detection.detectionId] ||
      cropImageFromDetection(detection, cameraImageElement, '');
    const cropPreview = cropImage
      ? undefined
      : cropPreviewFromDetection(detection, cameraImageElement, cameraImageFallbackSrc);
    return {
      id: remoteTempCropId(detection.detectionId),
      imageUrl: cropImage || REMOTE_DC_DRAFT_CROP_PLACEHOLDER_IMAGE,
      cropPreview,
      isInactive: false,
      isPending: false,
      storeId: detection.storeId ? String(detection.storeId) : undefined,
      itemNumber: base.itemNumber,
      source: 'forge',
      forgeOriginSource: 'remote-data-collection',
      barcode: normalizedBarcode,
      normalizedBarcode,
      normalizedUpc: normalizedBarcode,
      orientation: 'front',
      classificationType: 'product',
      remoteDataCollectionDraftOrder: index,
    };
  });
}

function buildDraftUpc(
  draft: AssignmentDraft,
  item: ApiMasterLabelUPCData | null | undefined,
  detections: RemoteDataCollectionDetection[],
  cameraImageElement?: HTMLImageElement,
  cameraImageFallbackSrc?: string
): MasterUPCData {
  const base = buildDraftBaseUpc(draft, item);
  const tempCrops = buildRemoteTempCrops(
    draft,
    base,
    detections,
    cameraImageElement,
    cameraImageFallbackSrc
  );
  return {
    ...base,
    unassignedCrops: [...tempCrops, ...base.unassignedCrops],
  };
}

function removeMissingRemoteTempCrops(
  upc: MasterUPCData,
  desiredTempCropIds: Set<string>
): MasterUPCData {
  return {
    ...upc,
    designs: upc.designs
      .map((design) => {
        const crops = design.crops.filter(
          (crop) => !isRemoteTempCropId(crop.id) || desiredTempCropIds.has(crop.id)
        );
        const representativeCropId = crops.some((crop) => crop.id === design.representativeCropId)
          ? design.representativeCropId
          : (crops[0]?.id ?? null);
        return {
          ...design,
          crops,
          representativeCropId,
        };
      })
      .filter((design) => design.crops.length > 0 || design.persistedDesignId),
    pendingCrops: upc.pendingCrops?.filter(
      (crop) => !isRemoteTempCropId(crop.id) || desiredTempCropIds.has(crop.id)
    ),
    unassignedCrops: upc.unassignedCrops.filter(
      (crop) => !isRemoteTempCropId(crop.id) || desiredTempCropIds.has(crop.id)
    ),
    cartonCrops: upc.cartonCrops.filter(
      (crop) => !isRemoteTempCropId(crop.id) || desiredTempCropIds.has(crop.id)
    ),
    inactiveCrops: upc.inactiveCrops?.filter(
      (crop) => !isRemoteTempCropId(crop.id) || desiredTempCropIds.has(crop.id)
    ),
  };
}

function cropPreviewMatches(
  a: MasterCropData['cropPreview'],
  b: MasterCropData['cropPreview']
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (
    a.sourceImageUrl !== b.sourceImageUrl ||
    a.cropX !== b.cropX ||
    a.cropY !== b.cropY ||
    a.cropWidth !== b.cropWidth ||
    a.cropHeight !== b.cropHeight ||
    a.sourceWidth !== b.sourceWidth ||
    a.sourceHeight !== b.sourceHeight
  ) {
    return false;
  }

  const aPoints = a.polygonPoints ?? [];
  const bPoints = b.polygonPoints ?? [];
  return (
    aPoints.length === bPoints.length &&
    aPoints.every((point, index) => point.x === bPoints[index]?.x && point.y === bPoints[index]?.y)
  );
}

function refreshRemoteTempCropPreviewData(
  crop: MasterCropData,
  desiredTempCropById: Map<string, MasterCropData>
): MasterCropData {
  if (!isRemoteTempCropId(crop.id)) return crop;

  const desired = desiredTempCropById.get(crop.id);
  if (!desired) return crop;

  const desiredHasRenderedImage = desired.imageUrl !== REMOTE_DC_DRAFT_CROP_PLACEHOLDER_IMAGE;
  const imageUrl =
    desiredHasRenderedImage || crop.imageUrl === REMOTE_DC_DRAFT_CROP_PLACEHOLDER_IMAGE
      ? desired.imageUrl
      : crop.imageUrl;
  let cropPreview = crop.cropPreview;
  if (desired.cropPreview) {
    cropPreview = desired.cropPreview;
  } else if (desiredHasRenderedImage) {
    cropPreview = undefined;
  }

  if (
    crop.imageUrl === imageUrl &&
    crop.remoteDataCollectionDraftOrder === desired.remoteDataCollectionDraftOrder &&
    cropPreviewMatches(crop.cropPreview, cropPreview)
  ) {
    return crop;
  }

  return {
    ...crop,
    imageUrl,
    cropPreview,
    remoteDataCollectionDraftOrder: desired.remoteDataCollectionDraftOrder,
  };
}

function refreshRemoteTempCropPreviewArray(
  crops: MasterCropData[],
  desiredTempCropById: Map<string, MasterCropData>
): MasterCropData[] {
  let changed = false;
  const refreshedCrops = crops.map((crop) => {
    const refreshedCrop = refreshRemoteTempCropPreviewData(crop, desiredTempCropById);
    if (refreshedCrop !== crop) changed = true;
    return refreshedCrop;
  });
  return changed ? refreshedCrops : crops;
}

function refreshRemoteTempCropPreviews(
  upc: MasterUPCData,
  desiredTempCropById: Map<string, MasterCropData>
): MasterUPCData {
  let changed = false;
  const designs = upc.designs.map((design) => {
    const crops = refreshRemoteTempCropPreviewArray(design.crops, desiredTempCropById);
    if (crops === design.crops) return design;
    changed = true;
    return { ...design, crops };
  });
  const pendingCrops = upc.pendingCrops
    ? refreshRemoteTempCropPreviewArray(upc.pendingCrops, desiredTempCropById)
    : undefined;
  const unassignedCrops = refreshRemoteTempCropPreviewArray(
    upc.unassignedCrops,
    desiredTempCropById
  );
  const cartonCrops = refreshRemoteTempCropPreviewArray(upc.cartonCrops, desiredTempCropById);
  const inactiveCrops = upc.inactiveCrops
    ? refreshRemoteTempCropPreviewArray(upc.inactiveCrops, desiredTempCropById)
    : undefined;

  changed =
    changed ||
    pendingCrops !== upc.pendingCrops ||
    unassignedCrops !== upc.unassignedCrops ||
    cartonCrops !== upc.cartonCrops ||
    inactiveCrops !== upc.inactiveCrops;

  if (!changed) return upc;
  return {
    ...upc,
    designs,
    pendingCrops,
    unassignedCrops,
    cartonCrops,
    inactiveCrops,
  };
}

export function reconcileRemoteDraftTempCrops(
  currentUpcs: MasterUPCData[],
  draft: AssignmentDraft,
  item: ApiMasterLabelUPCData | null | undefined,
  detections: RemoteDataCollectionDetection[],
  cameraImageElement?: HTMLImageElement,
  cameraImageFallbackSrc?: string
): MasterUPCData[] {
  if (detections.length === 0) {
    return currentUpcs.map((upc) => removeMissingRemoteTempCrops(upc, new Set()));
  }

  const base = buildDraftBaseUpc(draft, item);
  const desiredTempCrops = buildRemoteTempCrops(
    draft,
    base,
    detections,
    cameraImageElement,
    cameraImageFallbackSrc
  );
  const desiredTempCropIds = new Set(desiredTempCrops.map((crop) => crop.id));
  const desiredTempCropById = new Map(desiredTempCrops.map((crop) => [crop.id, crop]));
  const current = currentUpcs[0] ?? buildDraftUpc(draft, item, [], cameraImageElement);
  const next = refreshRemoteTempCropPreviews(
    removeMissingRemoteTempCrops(current, desiredTempCropIds),
    desiredTempCropById
  );
  const currentCropIds = new Set(getAllCropsFromUpc(next).map((crop) => crop.id));
  const missingTempCrops = desiredTempCrops.filter((crop) => !currentCropIds.has(crop.id));

  if (missingTempCrops.length > 0) {
    next.unassignedCrops = [...missingTempCrops, ...next.unassignedCrops];
  }

  return [next, ...currentUpcs.slice(1)];
}

function draftPlacementSignature(placements: PreparedCropPlacement[]): string {
  return placements
    .map((placement) =>
      [
        placement.detectionId,
        placement.normalizedBarcode,
        placement.itemNumber ?? '',
        placement.designId ?? '',
        placement.orientation,
        placement.classificationType,
        placement.createDesignGroupId ?? '',
      ].join(':')
    )
    .sort()
    .join('|');
}

export function preparedCropPlacementSubmitGroupKey(placement: PreparedCropPlacement): string {
  if (placement.createDesignGroupId) return `new:${placement.createDesignGroupId}`;
  return [
    'existing-or-unassigned',
    placement.itemNumber ?? '',
    placement.normalizedBarcode,
    placement.designId ?? '',
  ].join(':');
}

function isUnassignedProductPlacement(placement: PreparedCropPlacement): boolean {
  return (
    placement.classificationType === 'product' &&
    !placement.designId &&
    !placement.createDesignGroupId
  );
}

export function hasSubmittableDraftPlacements(
  placements: PreparedCropPlacement[],
  initialPlacementSignature: string,
  selectedCropIds: Set<string>,
  hasSyncedInitialSelection = true,
  allowSelectedUnassignedProducts = false
): boolean {
  if (placements.length === 0) return false;
  if (draftPlacementSignature(placements) !== initialPlacementSignature) return true;
  if (!hasSyncedInitialSelection || (selectedCropIds.size > 0 && !allowSelectedUnassignedProducts))
    return false;
  return placements.some(isUnassignedProductPlacement);
}

export function remoteDcMasterLabelHighlightScrollKey(
  editorResetKey: string,
  selectedCropCount: number
): string | null {
  return selectedCropCount > 0 ? `selected-crops:${editorResetKey}` : null;
}

function RemoteDCPreparedMatchCard({
  draft,
  item,
  isLoading,
  detections,
  cameraImageElement,
  cameraImageFallbackSrc,
  cropSizePx,
  isFocused,
  saving,
  chainId,
  autoSelectCameraCrops,
  suppressedAutoSelectCropIds,
  onFocus,
  onAllowAutoSelectCrops,
  onSuppressAutoSelectCrops,
  onRemoveDetections,
  onDirtyChange,
  onRequestClose,
  onSubmit,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  draft: AssignmentDraft;
  item?: ApiMasterLabelUPCData | null;
  isLoading: boolean;
  detections: RemoteDataCollectionDetection[];
  cameraImageElement?: HTMLImageElement;
  cameraImageFallbackSrc?: string;
  cropSizePx: number;
  isFocused: boolean;
  saving: boolean;
  chainId: number;
  autoSelectCameraCrops: boolean;
  suppressedAutoSelectCropIds: string[];
  onFocus: () => void;
  onAllowAutoSelectCrops: (cropIds: string[]) => void;
  onSuppressAutoSelectCrops: (cropIds: string[]) => void;
  onRemoveDetections: (detectionIds: string[]) => void;
  onDirtyChange: (draftId: string, hasChanges: boolean) => void;
  onRequestClose: () => void;
  onSubmit: (payload: RemoteDCMasterLabelSavePayload) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const notify = useCallback(
    (_text: string, _type?: 'success' | 'warning' | 'error') => undefined,
    []
  );
  const editor = useMasterLabelEditor({ notify });
  const baseUpc = useMemo(() => buildDraftBaseUpc(draft, item), [draft, item]);
  const initialUpc = useMemo(
    () => buildDraftUpc(draft, item, detections, cameraImageElement, cameraImageFallbackSrc),
    [cameraImageElement, cameraImageFallbackSrc, detections, draft, item]
  );
  const tempCropIds = useMemo(
    () => detections.map((detection) => remoteTempCropId(detection.detectionId)),
    [detections]
  );
  const tempCropKey = useMemo(() => tempCropIds.join('|'), [tempCropIds]);
  const tempCropIdSet = useMemo(() => new Set(tempCropIds), [tempCropIds]);
  const suppressedAutoSelectCropIdSet = useMemo(
    () => new Set(suppressedAutoSelectCropIds),
    [suppressedAutoSelectCropIds]
  );
  const editorResetKey = useMemo(
    () =>
      [
        draft.id,
        baseUpc.snapshotVersion ?? '',
        baseUpc.itemNumber,
        baseUpc.normalizedUpc ?? '',
        baseUpc.lookupValue ?? '',
      ].join('|'),
    [
      baseUpc.itemNumber,
      baseUpc.lookupValue,
      baseUpc.normalizedUpc,
      baseUpc.snapshotVersion,
      draft.id,
    ]
  );
  const initializedEditorKeyRef = useRef<string | null>(null);
  const autoSelectedCropIdsRef = useRef<Set<string>>(new Set());
  const [pendingNewDesign, setPendingNewDesign] = useState<{
    itemNumber: string;
    selectedCropCount: number;
    availableBarcodes: MasterBarcodeEntry[];
    preserveSelection: boolean;
  } | null>(null);
  const [isReviewChooserOpen, setIsReviewChooserOpen] = useState(false);
  const upc = editor.upcData[0] || initialUpc;
  const changeCount = editor.diffSummary.totalChanges;
  const canSubmitDraft = changeCount > 0;
  const selectedCropIds = useMemo(() => [...editor.selectedCropIds], [editor.selectedCropIds]);
  const selectedRemoteDetectionIds = useMemo(
    () =>
      selectedCropIds
        .map(remoteDetectionIdFromTempCropId)
        .filter((detectionId): detectionId is string => Boolean(detectionId)),
    [selectedCropIds]
  );
  const selectedPersistedCropIds = useMemo(
    () => selectedCropIds.filter((cropId) => !isRemoteTempCropId(cropId)),
    [selectedCropIds]
  );

  useEffect(() => {
    editor.replaceAllUpcs([baseUpc], [initialUpc]);
    initializedEditorKeyRef.current = editorResetKey;
    autoSelectedCropIdsRef.current = new Set();
    // Reset the embedded editor only when the loaded item changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorResetKey]);

  useEffect(() => {
    if (initializedEditorKeyRef.current !== editorResetKey) return;
    autoSelectedCropIdsRef.current = new Set(
      [...autoSelectedCropIdsRef.current].filter((cropId) => tempCropIdSet.has(cropId))
    );
    editor.patchWorkingUpcData((current) =>
      reconcileRemoteDraftTempCrops(
        current,
        draft,
        item,
        detections,
        cameraImageElement,
        cameraImageFallbackSrc
      )
    );
    editor.setSelectedCropIds((current) => {
      let changed = false;
      const next = new Set<string>();
      current.forEach((cropId) => {
        if (!isRemoteTempCropId(cropId) || tempCropIdSet.has(cropId)) {
          next.add(cropId);
        } else {
          changed = true;
        }
      });
      return changed ? next : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraImageElement, cameraImageFallbackSrc, editorResetKey, tempCropKey]);

  useEffect(() => {
    if (!autoSelectCameraCrops) return;
    const cropIdsToAutoSelect = remoteCropIdsNeedingAutoSelection(
      tempCropIds,
      suppressedAutoSelectCropIdSet,
      autoSelectedCropIdsRef.current
    );
    if (cropIdsToAutoSelect.length === 0) return;
    cropIdsToAutoSelect.forEach((cropId) => autoSelectedCropIdsRef.current.add(cropId));
    editor.setSelectedCropIds((current) => {
      if (cropIdsToAutoSelect.every((cropId) => current.has(cropId))) return current;
      const next = new Set(current);
      cropIdsToAutoSelect.forEach((cropId) => next.add(cropId));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectCameraCrops, editorResetKey, suppressedAutoSelectCropIdSet, tempCropKey]);

  useEffect(() => {
    if (editor.selectedCropIds.size === 0) {
      setIsReviewChooserOpen(false);
    }
  }, [editor.selectedCropIds.size]);

  useEffect(() => {
    onDirtyChange(draft.id, canSubmitDraft);
  }, [canSubmitDraft, draft.id, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange(draft.id, false);
    },
    [draft.id, onDirtyChange]
  );

  const removeSelectedRemoteCrops = useCallback(() => {
    if (selectedRemoteDetectionIds.length === 0) return;
    const removedCropIds = new Set(selectedRemoteDetectionIds.map(remoteTempCropId));
    editor.patchWorkingUpcData(
      (current) =>
        current.map((candidateUpc) => ({
          ...candidateUpc,
          designs: candidateUpc.designs
            .map((design) => ({
              ...design,
              crops: design.crops.filter((crop) => !removedCropIds.has(crop.id)),
            }))
            .filter((design) => design.crops.length > 0 || design.persistedDesignId),
          pendingCrops: candidateUpc.pendingCrops?.filter((crop) => !removedCropIds.has(crop.id)),
          unassignedCrops: candidateUpc.unassignedCrops.filter(
            (crop) => !removedCropIds.has(crop.id)
          ),
          cartonCrops: candidateUpc.cartonCrops.filter((crop) => !removedCropIds.has(crop.id)),
          inactiveCrops: candidateUpc.inactiveCrops?.filter((crop) => !removedCropIds.has(crop.id)),
        })),
      'Remove camera crop'
    );
    onAllowAutoSelectCrops([...removedCropIds]);
    onRemoveDetections(selectedRemoteDetectionIds);
    editor.setSelectedCropIds(new Set(selectedPersistedCropIds));
  }, [
    editor,
    onAllowAutoSelectCrops,
    onRemoveDetections,
    selectedRemoteDetectionIds,
    selectedPersistedCropIds,
  ]);

  const handleDeleteSelected = useCallback(
    (options?: RemoteDCMutationOptions) => {
      if (selectedRemoteDetectionIds.length > 0) {
        removeSelectedRemoteCrops();
        return;
      }
      if (selectedPersistedCropIds.length > 0) {
        editor.markSelectedForDeletion(options);
      }
    },
    [editor, removeSelectedRemoteCrops, selectedPersistedCropIds, selectedRemoteDetectionIds.length]
  );

  const openNewDesignBarcodeDialog = useCallback(
    (itemNumber: string, options?: RemoteDCMutationOptions) => {
      const selectedCrops = getSelectedCrops(upc, editor.selectedCropIds);
      if (selectedCrops.length === 0) return;
      const preserveSelection = Boolean(options?.preserveSelection);
      if (remoteDcCanCreateNewDesignDirectly(selectedCrops)) {
        editor.createNewDesign(itemNumber, { preserveSelection });
        return;
      }
      const availableBarcodes = getRemoteDcNewDesignBarcodes(upc);
      if (availableBarcodes.length === 0) {
        notify('No barcodes available - cannot create design without choosing a barcode', 'error');
        return;
      }
      setPendingNewDesign({
        itemNumber,
        selectedCropCount: selectedCrops.length,
        availableBarcodes,
        preserveSelection,
      });
    },
    [editor, notify, upc]
  );

  const openReviewChooser = useCallback(() => {
    if (editor.selectedCropIds.size === 0) return;
    setIsReviewChooserOpen(true);
  }, [editor.selectedCropIds.size]);

  const closeReviewChooser = useCallback(() => {
    setIsReviewChooserOpen(false);
  }, []);

  const applyReviewIssue = useCallback(
    (reviewIssueType: MasterLabelReviewIssueType, options?: RemoteDCMutationOptions) => {
      editor.setSelectedReviewIssue(reviewIssueType, options);
      setIsReviewChooserOpen(false);
    },
    [editor]
  );

  const clearReviewIssue = useCallback(
    (options?: RemoteDCMutationOptions) => {
      editor.clearSelectedReviewIssue(options);
      setIsReviewChooserOpen(false);
    },
    [editor]
  );

  const handleCropClick = useCallback(
    (cropId: string, shiftKey: boolean) => {
      const wasSelected = editor.selectedCropIds.has(cropId);
      editor.toggleCropSelection(cropId, shiftKey);
      if (isRemoteTempCropId(cropId) && wasSelected && !shiftKey) {
        onSuppressAutoSelectCrops([cropId]);
      }
    },
    [editor, onSuppressAutoSelectCrops]
  );

  const remoteCrops = useMemo<MasterDataCollectionRemoteCropMaterialization[]>(() => {
    const currentCropsById = new Map(getAllCropsFromUpc(upc).map((crop) => [crop.id, crop]));
    return detections.map((detection) => {
      const tempCropId = remoteTempCropId(detection.detectionId);
      const crop = currentCropsById.get(tempCropId);
      return {
        temp_crop_id: tempCropId,
        shape_list: detection.shapeList,
        index: detection.index,
        normalized_barcode:
          crop?.normalizedBarcode ||
          crop?.normalizedUpc ||
          crop?.barcode ||
          draft.context.normalizedBarcode ||
          draft.context.normalizedBarcodes[0] ||
          null,
        item_number: upc.actualItemNumber || upc.itemNumber || draft.context.itemNumber || null,
        orientation: crop?.orientation === 'not-front' ? 'not-front' : 'front',
        classification_type: crop?.classificationType === 'carton' ? 'carton' : 'product',
      };
    });
  }, [detections, draft.context, upc]);

  const submitMasterLabelState = useCallback(() => {
    if (!canSubmitDraft || saving || isLoading) return;
    const debugInfo = buildMasterLabelDebugInfo(chainId, editor.baseUpcData, editor.upcData);
    if (debugInfo.request.items.length === 0) return;
    const submittedDetectionIds = remoteCrops
      .filter((crop) => JSON.stringify(debugInfo.request).includes(crop.temp_crop_id))
      .map((crop) => remoteDetectionIdFromTempCropId(crop.temp_crop_id))
      .filter((detectionId): detectionId is string => Boolean(detectionId));
    onSuppressAutoSelectCrops(submittedDetectionIds.map(remoteTempCropId));
    onSubmit({
      state: debugInfo.request,
      remoteCrops,
      submittedDetectionIds,
    });
  }, [
    canSubmitDraft,
    chainId,
    editor.baseUpcData,
    editor.upcData,
    isLoading,
    onSubmit,
    onSuppressAutoSelectCrops,
    remoteCrops,
    saving,
  ]);

  useEffect(() => {
    if (!isFocused) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      )
        return;
      const key = event.key.toLowerCase();
      if (isReviewChooserOpen) {
        if (key === 'escape') {
          event.preventDefault();
          closeReviewChooser();
          return;
        }
        const reviewAction = reviewIssueActionFromShortcutKey(event.key);
        if (reviewAction === 'clear') {
          event.preventDefault();
          clearReviewIssue(remoteDcShortcutMutationOptions(event));
          return;
        }
        if (reviewAction) {
          event.preventDefault();
          applyReviewIssue(reviewAction, remoteDcShortcutMutationOptions(event));
          return;
        }
        return;
      }
      if (pendingNewDesign) {
        if (key === 'escape') {
          event.preventDefault();
          setPendingNewDesign(null);
        }
        return;
      }
      if ((event.metaKey || event.ctrlKey) && key === 'z') {
        event.preventDefault();
        editor.undo();
      } else if ((event.metaKey || event.ctrlKey) && key === 's') {
        event.preventDefault();
        submitMasterLabelState();
      } else if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      } else if (key === 'd' && editor.selectedCropIds.size > 0) {
        event.preventDefault();
        openNewDesignBarcodeDialog(upc.itemNumber, remoteDcShortcutMutationOptions(event));
      } else if (key === 'r' && editor.selectedCropIds.size > 0) {
        event.preventDefault();
        openReviewChooser();
      } else if (key === 'f') {
        event.preventDefault();
        editor.setSelectedCropOrientation('front', remoteDcShortcutMutationOptions(event));
      } else if (key === 'n') {
        event.preventDefault();
        editor.setSelectedCropOrientation('not-front', remoteDcShortcutMutationOptions(event));
      } else if (key === 'i' && editor.selectedCropIds.size > 0) {
        event.preventDefault();
        editor.deactivateCrops(remoteDcShortcutMutationOptions(event));
      } else if (key === 'c') {
        event.preventDefault();
        editor.handleSectionClick(
          { itemNumber: upc.itemNumber, type: 'carton' },
          remoteDcShortcutMutationOptions(event)
        );
      } else if (
        key === 's' &&
        editor.selectedCropDesignInfo &&
        !editor.selectedCropDesignInfo.isAlreadyRep
      ) {
        event.preventDefault();
        editor.setRepresentative(
          editor.selectedCropDesignInfo.designId,
          editor.selectedCropDesignInfo.cropId,
          remoteDcShortcutMutationOptions(event)
        );
      } else if ((key === 'delete' || key === 'backspace') && editor.selectedCropIds.size > 0) {
        event.preventDefault();
        handleDeleteSelected(remoteDcShortcutMutationOptions(event));
      } else if (key === 'escape') {
        event.preventDefault();
        onRequestClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    applyReviewIssue,
    clearReviewIssue,
    closeReviewChooser,
    editor,
    handleDeleteSelected,
    isReviewChooserOpen,
    isFocused,
    onRequestClose,
    openNewDesignBarcodeDialog,
    openReviewChooser,
    pendingNewDesign,
    submitMasterLabelState,
    upc.itemNumber,
  ]);

  const handleSubmit = () => {
    submitMasterLabelState();
  };

  return (
    <article className={`mdc-confirm-card${isFocused ? ' is-focused' : ''}`} onMouseDown={onFocus}>
      {isLoading ? (
        <RemoteDataCollectionLoadingCard compact />
      ) : (
        <div className="mdc-remote-upc-shell">
          <UPCColumnView
            upc={upc}
            baseUpc={baseUpc}
            selectedCropIds={editor.selectedCropIds}
            onCropClick={handleCropClick}
            onSectionClick={editor.handleSectionClick}
            mode={editor.mode}
            mergeSourceDesignId={null}
            activeDesignId={editor.activeDesignId}
            onActiveDesignChange={editor.setActiveDesignId}
            designColorMap={editor.designColorMap}
            recentlyMovedCropIds={editor.recentlyMovedCropIds}
            selectionVisualMode="master-label"
            cropOrderMode="master-label"
            cropSize={cropSizePx}
            textScale={86}
            highlightedCropIds={
              editor.selectedCropIds.size > 0 ? editor.selectedCropIds : undefined
            }
            highlightScrollKey={remoteDcMasterLabelHighlightScrollKey(
              editorResetKey,
              editor.selectedCropIds.size
            )}
            highlightScrollAlignment="start"
            onImageHover={onImageHover}
            onImageMove={onImageMove}
            onImageLeave={onImageLeave}
            onNotify={notify}
            showEmptyPendingSection={false}
            showRemoteDataCollectionButton={false}
          />
          <CropActionBar
            mode={editor.mode}
            selectedCropIds={editor.selectedCropIds}
            newDesignOptions={[{ itemNumber: upc.itemNumber, label: 'New Design' }]}
            onDeactivate={editor.deactivateCrops}
            onSetRep={
              editor.selectedCropDesignInfo && !editor.selectedCropDesignInfo.isAlreadyRep
                ? editor.selectedCropDesignInfo
                : null
            }
            onSetRepClick={editor.setRepresentative}
            onNewDesignClick={openNewDesignBarcodeDialog}
            onReviewClick={openReviewChooser}
            onDeleteClick={editor.selectedCropIds.size > 0 ? handleDeleteSelected : undefined}
            undoCount={editor.undoStack.length}
            onUndo={editor.undo}
            layoutVariant="master-label"
            textScale={86}
            escapeActionLabel="Close"
            idleContent={
              <span className="mdc-confirm-hint">
                Crops start in Unassigned Products. Select crops, then use D/R/F/N/C/I or click a
                section.
              </span>
            }
          />
          {pendingNewDesign ? (
            <RemoteDcNewDesignBarcodeDialog
              itemNumber={pendingNewDesign.itemNumber}
              selectedCropCount={pendingNewDesign.selectedCropCount}
              availableBarcodes={pendingNewDesign.availableBarcodes}
              onConfirm={(normalizedBarcode) => {
                const targetItemNumber = pendingNewDesign.itemNumber;
                const preserveSelection = pendingNewDesign.preserveSelection;
                setPendingNewDesign(null);
                editor.createNewDesign(targetItemNumber, { normalizedBarcode, preserveSelection });
              }}
              onClose={() => setPendingNewDesign(null)}
            />
          ) : null}
          {isReviewChooserOpen ? (
            <ReviewIssueChooserOverlay
              onSelect={(reviewIssueType) => applyReviewIssue(reviewIssueType)}
              onClear={() => clearReviewIssue()}
              onClose={closeReviewChooser}
            />
          ) : null}
          {canSubmitDraft || saving ? (
            <div className="mdc-prepared-save-row">
              <button
                type="button"
                className="mdc-submit-prepared"
                disabled={saving || isLoading}
                onClick={handleSubmit}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </article>
  );
}

type ProductSearchResultEntry = {
  product: StoreProductSearchResult;
  embeddingCount?: number | null;
};

function searchedProductAssignmentTarget(product: StoreProductSearchResult): AssignmentContext {
  const normalizedBarcode = product.upc || null;
  return {
    source: 'candidate',
    itemNumber: product.itemNumber || null,
    normalizedBarcode,
    normalizedBarcodes: normalizedBarcode ? [normalizedBarcode] : [],
    label: product.name || product.itemNumber || normalizedBarcode || product.upc,
  };
}

function ProductResultCard({
  product,
  embeddingCount,
  chainId,
  assignLabel,
  assignDisabled,
  onAssign,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  product: StoreProductSearchResult;
  embeddingCount?: number | null;
  chainId?: number;
  assignLabel?: string;
  assignDisabled?: boolean;
  onAssign: () => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const barcode = product.upc || null;
  const itemNumber = product.itemNumber || null;
  const productTitle = product.name || itemNumber || barcode || product.upc;
  const showItemNumber = Boolean(itemNumber && itemNumber !== productTitle);
  const masterLabelHref = chainId ? masterLabelUrl(chainId, itemNumber, barcode) : null;
  const productImageUrl = safeImageUrl(product.imageUrl);
  const [productImageFailed, setProductImageFailed] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    setProductImageFailed(false);
  }, [productImageUrl]);

  return (
    <article className="mdc-candidate-card">
      <div className="mdc-candidate-body">
        <div className="mdc-candidate-title-row">
          <div className="mdc-candidate-title-stack">
            <div className="mdc-candidate-name">
              {productTitle}
              {typeof embeddingCount === 'number' ? (
                <span className="mdc-product-embedding-count">({embeddingCount})</span>
              ) : null}
              {product.isActive === false ? (
                <span className="mdc-product-inactive-badge">Inactive</span>
              ) : null}
            </div>
            {showItemNumber ? <div className="mdc-candidate-item-number">{itemNumber}</div> : null}
          </div>
          <div className="mdc-candidate-product-image-slot">
            {productImageUrl && !productImageFailed ? (
              <img
                src={productImageUrl}
                alt={productTitle}
                className="mdc-candidate-product-image"
                style={{ cursor: 'zoom-in' }}
                onClick={() => setZoomOpen(true)}
                onMouseEnter={
                  onImageHover
                    ? (event) =>
                        onImageHover(event, productImageUrl, {
                          id: itemNumber || barcode || product.upc,
                          normalizedBarcode: barcode ?? undefined,
                        })
                    : undefined
                }
                onMouseMove={
                  onImageMove
                    ? (event) =>
                        onImageMove(event, productImageUrl, {
                          id: itemNumber || barcode || product.upc,
                          normalizedBarcode: barcode ?? undefined,
                        })
                    : undefined
                }
                onMouseLeave={onImageLeave}
                onError={() => setProductImageFailed(true)}
              />
            ) : null}
          </div>
        </div>
        {productImageUrl ? (
          <ImageZoomModal
            isOpen={zoomOpen}
            imageSrc={productImageUrl}
            onClose={() => setZoomOpen(false)}
          />
        ) : null}
        <div className="mdc-candidate-identity">
          {barcode ? (
            <div>
              <span>UPC</span>
              <strong>{barcode}</strong>
            </div>
          ) : null}
        </div>
        <div className="mdc-candidate-links">
          <button
            type="button"
            className="mdc-candidate-assign"
            disabled={assignDisabled}
            onClick={onAssign}
          >
            {assignLabel || 'Assign'}
          </button>
          <div className="mdc-candidate-link-actions">
            {masterLabelHref ? (
              <MasterLabelLink
                to={masterLabelHref}
                title="Open in Forge Master Label"
                aria-label="Open in Forge Master Label"
              >
                [f]
              </MasterLabelLink>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function ProductSearchPanel({
  detection,
  storeName,
  chainId,
  selectedDetectionCount,
  isAssigningCandidate,
  onAssignCandidate,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  detection: RemoteDataCollectionDetection;
  storeName?: string | null;
  chainId?: number;
  selectedDetectionCount: number;
  isAssigningCandidate: boolean;
  onAssignCandidate: (detection: RemoteDataCollectionDetection, target: AssignmentContext) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const { token, legacyToken } = useAuthContext();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<ProductSearchResultEntry[]>([]);

  const runSearch = useCallback(async () => {
    const search = query.trim();
    if (!search) {
      setError('Enter a name or barcode to search');
      return;
    }
    if (!storeName) {
      setError('Store is not available for this detection');
      return;
    }
    setLoading(true);
    setError(null);
    setResults([]);
    setSearched(true);
    try {
      const response = await ApiService.searchStoreProducts(legacyToken, storeName, search, 12);
      const products = response.products ?? [];

      // Fetch each product's embedding count to display it and to sort by it
      // (fewest embeddings first) after the active/inactive split.
      const embeddingCountByBarcode = new Map<string, number>();
      if (products.length && chainId) {
        const barcodes = [...new Set(products.map((product) => product.upc).filter(Boolean))];
        if (barcodes.length) {
          try {
            const counts = await ApiService.getEmbeddingCountsByBarcode(token, chainId, barcodes);
            counts.forEach((entry) =>
              embeddingCountByBarcode.set(entry.barcode, entry.embeddingsCount)
            );
          } catch {
            // Fall back to active-only sort if counts are unavailable.
          }
        }
      }

      const embeddingCountFor = (product: StoreProductSearchResult) =>
        embeddingCountByBarcode.get(product.upc) ?? 0;

      // Active products first, then by ascending embedding count.
      const sorted = [...products]
        .sort((a, b) => {
          const activeDelta = (a.isActive === false ? 1 : 0) - (b.isActive === false ? 1 : 0);
          if (activeDelta !== 0) return activeDelta;
          return embeddingCountFor(a) - embeddingCountFor(b);
        })
        .map((product) => ({
          product,
          embeddingCount: embeddingCountByBarcode.has(product.upc)
            ? embeddingCountByBarcode.get(product.upc)
            : null,
        }));
      setResults(sorted);
    } catch {
      setError('Error searching for product');
    } finally {
      setLoading(false);
    }
  }, [query, storeName, legacyToken, token, chainId]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSearched(false);
    setError(null);
  }, []);

  const assignLabel = isAssigningCandidate
    ? 'Assigning...'
    : selectedDetectionCount > 0
      ? 'Assign selected'
      : 'Assign to this item';

  return (
    <div className="mdc-product-search">
      <div className="mdc-product-search-body">
        <form
          className="mdc-product-search-form"
          onSubmit={(event) => {
            event.preventDefault();
            void runSearch();
          }}
        >
          <SearchOutlinedIcon className="mdc-product-search-icon" style={{ fontSize: 18 }} />
          <input
            type="text"
            className="mdc-product-search-input"
            placeholder="Search product by name or item number or barcode"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            type="submit"
            className="mdc-product-search-submit"
            disabled={loading || !query.trim()}
          >
            {loading ? (
              <>
                <span
                  className="mdc-loading-spinner mdc-product-search-spinner"
                  aria-hidden="true"
                />
                Searching...
              </>
            ) : (
              'Search'
            )}
          </button>
          {searched || query ? (
            <button
              type="button"
              className="mdc-product-search-clear"
              onClick={clearSearch}
              title="Clear search"
              aria-label="Clear search"
            >
              <CloseOutlinedIcon style={{ fontSize: 18 }} />
            </button>
          ) : null}
        </form>
        {error ? <div className="mdc-detail-empty">{error}</div> : null}
        {!error && searched && !loading && results.length === 0 ? (
          <div className="mdc-detail-empty">Product not found</div>
        ) : null}
        {results.length ? (
          <div className="mdc-candidate-grid mdc-product-search-results">
            {results.map(({ product, embeddingCount }) => (
              <ProductResultCard
                key={product.productId ?? product.upc}
                product={product}
                embeddingCount={embeddingCount}
                chainId={chainId}
                assignLabel={assignLabel}
                assignDisabled={isAssigningCandidate}
                onAssign={() =>
                  void onAssignCandidate(detection, searchedProductAssignmentTarget(product))
                }
                onImageHover={onImageHover}
                onImageMove={onImageMove}
                onImageLeave={onImageLeave}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DetectionDetailsContent({
  detection,
  storeName,
  cameraImageElement,
  cameraImageBase64,
  chainId,
  selectedDetectionCount,
  isAssigningCandidate,
  onAssignCandidate,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  detection: RemoteDataCollectionDetection;
  storeName?: string | null;
  cameraImageElement?: HTMLImageElement;
  cameraImageBase64?: string;
  chainId?: number;
  selectedDetectionCount: number;
  isAssigningCandidate: boolean;
  onAssignCandidate: (detection: RemoteDataCollectionDetection, target: AssignmentContext) => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const { token } = useAuthContext();
  const previewBox = detailDetectionBox(detection);
  const topCandidateGroups = useMemo(() => detection.candidateGroups.slice(0, 3), [detection]);
  const candidateContexts = useMemo(
    () =>
      topCandidateGroups.map((group) => ({
        group,
        context: candidateAssignmentTarget(group),
      })),
    [topCandidateGroups]
  );
  const candidateContextKey = useMemo(
    () => candidateContexts.map(({ context }) => assignmentContextIdentity(context)).join('|'),
    [candidateContexts]
  );
  const sampleDesignNameEntries = useMemo(
    () =>
      topCandidateGroups
        .flatMap((group) => (group.referenceCrops ?? []).slice(0, 3))
        .map((crop) => (crop.designId && crop.designName ? [crop.designId, crop.designName] : null))
        .filter((value): value is [string, string] => Boolean(value)),
    [topCandidateGroups]
  );
  const sampleDesignNamesById = useMemo(
    () => Object.fromEntries(sampleDesignNameEntries),
    [sampleDesignNameEntries]
  );
  const [candidateItemsByKey, setCandidateItemsByKey] = useState<
    Record<string, ApiMasterLabelUPCData | null>
  >({});

  useEffect(() => {
    let cancelled = false;
    setCandidateItemsByKey({});
    if (!chainId || candidateContexts.length === 0) return undefined;

    const itemNumbers = [
      ...new Set(
        candidateContexts
          .map(({ context }) => context.itemNumber)
          .filter((value): value is string => Boolean(value))
      ),
    ];
    const normalizedBarcodes = [
      ...new Set(
        candidateContexts
          .flatMap(({ context }) =>
            context.normalizedBarcodes.length
              ? context.normalizedBarcodes
              : context.normalizedBarcode
                ? [context.normalizedBarcode]
                : []
          )
          .filter((value): value is string => Boolean(value))
      ),
    ];

    if (itemNumbers.length === 0 && normalizedBarcodes.length === 0) {
      return undefined;
    }

    ForgeApiService.resolveMasterLabelLinkTargets(token, {
      chainId,
      itemNumbers,
      normalizedBarcodes,
      designIds: [],
    })
      .then((response) => {
        if (cancelled) return;
        const itemsByItemNumber = new Map<string, ApiMasterLabelUPCData>();
        const itemsByBarcode = new Map<string, ApiMasterLabelUPCData>();
        response.items.forEach((item) => {
          if (item.itemNumber) itemsByItemNumber.set(item.itemNumber, item);
          if (item.normalizedUpc) itemsByBarcode.set(item.normalizedUpc, item);
          if (item.upc) itemsByBarcode.set(item.upc, item);
        });

        const itemsByTarget = new Map<string, ApiMasterLabelUPCData>();
        response.resolvedTargets.forEach((target) => {
          const item =
            (target.itemNumber ? itemsByItemNumber.get(target.itemNumber) : undefined) ||
            (target.normalizedBarcode ? itemsByBarcode.get(target.normalizedBarcode) : undefined);
          if (item) itemsByTarget.set(`${target.targetType}:${target.targetValue}`, item);
        });

        const next: Record<string, ApiMasterLabelUPCData | null> = {};
        candidateContexts.forEach(({ context }) => {
          const key = assignmentContextIdentity(context);
          let item: ApiMasterLabelUPCData | null =
            (context.itemNumber
              ? itemsByTarget.get(`item_number:${context.itemNumber}`) ||
                itemsByItemNumber.get(context.itemNumber) ||
                null
              : null) || null;
          if (!item) {
            const barcodes = context.normalizedBarcodes.length
              ? context.normalizedBarcodes
              : context.normalizedBarcode
                ? [context.normalizedBarcode]
                : [];
            item =
              barcodes
                .map(
                  (barcode) =>
                    itemsByTarget.get(`normalized_barcode:${barcode}`) ||
                    itemsByBarcode.get(barcode) ||
                    null
                )
                .find((candidate): candidate is ApiMasterLabelUPCData => Boolean(candidate)) ||
              null;
          }
          next[key] = item;
        });
        setCandidateItemsByKey(next);
      })
      .catch(() => {
        if (!cancelled) setCandidateItemsByKey({});
      });

    return () => {
      cancelled = true;
    };
  }, [candidateContexts, candidateContextKey, chainId, token]);

  return (
    <div className="mdc-modal-body mdc-modal-body--detection">
      <div className="mdc-detection-stage">
        <div className="mdc-detail-preview">
          <DetailCropPreview
            imageElement={cameraImageElement}
            imageBase64={cameraImageBase64}
            crop={bboxCropFromDetection(detection)}
            boxes={[previewBox]}
            compact
            zoomImageUrl={
              cropImageFromDetection(detection, cameraImageElement, cameraImageBase64) || undefined
            }
            zoomMeta={{
              id: detection.detectionId,
              storeId: detection.storeId ? String(detection.storeId) : undefined,
              classificationType: detection.classificationType,
            }}
            onImageHover={onImageHover}
            onImageMove={onImageMove}
            onImageLeave={onImageLeave}
          />
        </div>
        <DetectionSummaryPanel detection={detection} />
      </div>
      <ProductSearchPanel
        detection={detection}
        storeName={storeName}
        chainId={chainId}
        selectedDetectionCount={selectedDetectionCount}
        isAssigningCandidate={isAssigningCandidate}
        onAssignCandidate={onAssignCandidate}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
      {topCandidateGroups.length ? (
        <>
          <div className="mdc-candidate-grid">
            {topCandidateGroups.map((group, index) => {
              const referenceCrops = group.referenceCrops ?? [];
              const context = candidateAssignmentTarget(group);
              const item = candidateItemsByKey[assignmentContextIdentity(context)];
              return (
                <CandidatePreviewCard
                  key={`${group.keyKind}:${group.key}`}
                  group={group}
                  item={item}
                  referenceCrops={referenceCrops}
                  rank={index + 1}
                  chainId={chainId}
                  storeId={detection.storeId}
                  assignLabel={
                    isAssigningCandidate
                      ? 'Assigning...'
                      : selectedDetectionCount > 0
                        ? 'Assign selected'
                        : 'Assign to this item'
                  }
                  assignDisabled={isAssigningCandidate}
                  sampleDesignNamesById={sampleDesignNamesById}
                  onAssign={() =>
                    void onAssignCandidate(detection, candidateAssignmentTarget(group, item))
                  }
                  onImageHover={onImageHover}
                  onImageMove={onImageMove}
                  onImageLeave={onImageLeave}
                />
              );
            })}
          </div>
        </>
      ) : (
        <div className="mdc-detail-empty">No candidates returned for this detection.</div>
      )}
    </div>
  );
}

function PogDetailsContent({
  bbox,
  pog,
  imageElement,
  imageBase64,
  chainId,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  bbox: PogBBox;
  pog?: CameraPog;
  imageElement?: HTMLImageElement;
  imageBase64?: string;
  chainId?: number;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  const itemNumber = bbox.itemNumber ? String(bbox.itemNumber) : undefined;
  const upc = bbox.upc ? String(bbox.upc) : undefined;
  const label = bbox.name || pog?.tagName || bbox.tagName || 'POG item';
  const link = chainId && (itemNumber || upc) ? masterLabelUrl(chainId, itemNumber, upc) : null;

  return (
    <div className="mdc-modal-body mdc-pog-detail-body">
      <div className="mdc-pog-detail-preview">
        <PogImageDetailPreview
          bbox={bbox}
          imageElement={imageElement}
          imageBase64={imageBase64}
          onImageHover={onImageHover}
          onImageMove={onImageMove}
          onImageLeave={onImageLeave}
        />
      </div>
      <div className="mdc-pog-detail-facts">
        <PogDetailRow label="Name / tag" value={label} />
        <PogDetailRow label="Item number" value={itemNumber} />
        <PogDetailRow label="UPC" value={upc} />
        <PogDetailRow label="Shelf" value={bbox.shelf} />
        <PogDetailRow label="Mod" value={pog?.modCoverageId || bbox.modCoverageId} />
        <PogDetailRow label="Column" value={bbox.column} />
        <PogDetailRow
          label="Embedding crops"
          value={bbox.embeddingCropCount ?? bbox.embedding_crop_count}
        />
      </div>
      {link ? (
        <div className="mdc-modal-actions">
          <MasterLabelLink to={link} className="mdc-modal-link">
            Master Label <OpenInNewOutlinedIcon style={{ fontSize: 14 }} />
          </MasterLabelLink>
        </div>
      ) : null}
    </div>
  );
}

function PogProductDetailsPanel({
  item,
  chainId,
  onClose,
  onImageHover,
  onImageMove,
  onImageLeave,
}: {
  item: Extract<DetailModalItem, { kind: 'pog' }>;
  chainId?: number;
  onClose: () => void;
  onImageHover?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageMove?: (e: React.MouseEvent, url: string, meta?: ZoomMeta) => void;
  onImageLeave?: () => void;
}): JSX.Element {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose]);

  const previewImageElement = (item.bbox as any).previewImageElement as
    | HTMLImageElement
    | undefined;
  const previewImageBase64 = (item.bbox as any).previewImageBase64 as string | undefined;
  const imageBase64 = previewImageBase64 || item.bbox.pogImageUri || item.pog?.pogImageUri;

  return (
    <aside
      className="mdc-pog-details-panel"
      role="dialog"
      aria-modal="false"
      aria-label="POG product details"
    >
      <header className="mdc-modal-header">
        <div>
          <h2>POG product details</h2>
        </div>
        <div className="mdc-modal-header-actions">
          <button
            type="button"
            className="mdc-modal-close"
            aria-label="Close details"
            onClick={onClose}
          >
            <CloseOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </header>
      <PogDetailsContent
        bbox={item.bbox}
        pog={item.pog}
        imageElement={previewImageElement}
        imageBase64={imageBase64}
        chainId={chainId}
        onImageHover={onImageHover}
        onImageMove={onImageMove}
        onImageLeave={onImageLeave}
      />
    </aside>
  );
}

export const MasterDataCollectionPage: React.FC = () => {
  const { token, hasInternalAccess, isNonAdminPreview } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const viewportHeight = useViewportHeight();
  const {
    handleImageHover,
    handleImageMove,
    handleImageLeave,
    ZoomOverlay,
    zoomEnabled,
    toggleZoom,
  } = useZoomPreview();
  const inferenceId = searchParams.get('inference') || '';
  const imageIdParam = searchParams.get('image_id')
    ? Number(searchParams.get('image_id'))
    : undefined;
  const shouldAutoLoadImageInference = searchParams.get('load_pr') === '1';
  const chainId = searchParams.get('chain') ? Number(searchParams.get('chain')) : undefined;
  const queryItemNumber = searchParams.get('in');
  const queryBarcode = searchParams.get('nb');
  const [detail, setDetail] = useState<MasterDataCollectionDetailResponse | null>(null);
  const [preview, setPreview] = useState<MasterDataCollectionImagePreviewResponse | null>(null);
  const [imageTimeline, setImageTimeline] =
    useState<RemoteDataCollectionImageTimelineResponse | null>(null);
  const [selectedTimelineImageId, setSelectedTimelineImageId] = useState<number | null>(
    imageIdParam ?? null
  );
  const autoLoadImageInferenceKeyRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isDefaultsLoading, setIsDefaultsLoading] = useState(true);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [cachedInferenceStatus, setCachedInferenceStatus] = useState<
    'idle' | 'checking' | 'hit' | 'miss' | 'error'
  >('idle');
  const [isLoadingInferenceForImage, setIsLoadingInferenceForImage] = useState(false);
  const [prLoadError, setPrLoadError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageLookupDraft, setImageLookupDraft] = useState('');
  const [imageLookupError, setImageLookupError] = useState<string | null>(null);
  const [similarityThresholdPct, setSimilarityThresholdPct] = useState(
    REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT
  );
  const [deltaThresholdPct, setDeltaThresholdPct] = useState(REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT);
  const [ssmConfidencePct, setSsmConfidencePct] = useState(
    REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT
  );
  const [useSimilarityThreshold, setUseSimilarityThreshold] = useState(true);
  const [useDeltaThreshold, setUseDeltaThreshold] = useState(true);
  const [useSsmConfidenceThreshold, setUseSsmConfidenceThreshold] = useState(true);
  const [mergeLinkedCandidates, setMergeLinkedCandidates] = useState(
    REMOTE_DC_DEFAULT_MERGE_LINKED_CANDIDATES
  );
  const [visibleDetectionType, setVisibleDetectionType] = useState<DetectionTypeFilter>('both');
  const [hiddenPogImageKeys, setHiddenPogImageKeys] = useState<Set<string>>(() => new Set());
  const [polygonStrokeWidth, setPolygonStrokeWidth] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.polygonStrokeWidth,
      9,
      POLYGON_STROKE_RANGE.min,
      POLYGON_STROKE_RANGE.max
    )
  );
  const [overlayOpacityPct, setOverlayOpacityPct] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.overlayOpacityPct,
      18,
      OVERLAY_OPACITY_RANGE.min,
      OVERLAY_OPACITY_RANGE.max
    )
  );
  const [imagePanePct, setImagePanePct] = useState(() =>
    readStoredNumber(STORAGE_KEYS.imagePanePct, 68, IMAGE_PANE_RANGE.min, IMAGE_PANE_RANGE.max)
  );
  const [autoImageArea, setAutoImageArea] = useState(() =>
    readStoredBoolean(STORAGE_KEYS.autoImageArea, false)
  );
  const [workspaceZoomPct, setWorkspaceZoomPct] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.workspaceZoomPct,
      100,
      WORKSPACE_ZOOM_RANGE.min,
      WORKSPACE_ZOOM_RANGE.max
    )
  );
  const [cropSizePx, setCropSizePx] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.cropSizePx,
      REMOTE_DC_CROP_SIZE_PX,
      MASTER_LABEL_IMAGE_SLIDER_MIN,
      MASTER_LABEL_IMAGE_SLIDER_MAX
    )
  );
  const [controlsMode, setControlsMode] = useState<RemoteDcControlsMode>(() =>
    readStoredControlsMode()
  );
  const [autoCloseMasterLabelOnSave, setAutoCloseMasterLabelOnSave] = useState(() =>
    readStoredBoolean(STORAGE_KEYS.autoCloseMasterLabelOnSave, true)
  );
  const [autoSelectCameraCrops, setAutoSelectCameraCrops] = useState(() =>
    readStoredBoolean(STORAGE_KEYS.autoSelectCameraCrops, true)
  );
  const [selectedDetectionIds, setSelectedDetectionIds] = useState<Set<string>>(() => new Set());
  const selectedDetectionIdsRef = useRef<Set<string>>(new Set());
  const updateSelectedDetectionIds = useCallback(
    (updater: Set<string> | ((current: Set<string>) => Set<string>)) => {
      setSelectedDetectionIds((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater;
        selectedDetectionIdsRef.current = next;
        return next;
      });
    },
    []
  );
  const [rightPaneMode, setRightPaneMode] = useState<RemoteDcPaneMode>('pog');
  const [assignmentContext, setAssignmentContext] = useState<AssignmentContext | null>(null);
  const [assignmentDrafts, setAssignmentDrafts] = useState<AssignmentDraft[]>([]);
  const [focusedDraftId, setFocusedDraftId] = useState<string | null>(null);
  const [dirtyDraftIds, setDirtyDraftIds] = useState<Set<string>>(() => new Set());
  const [pendingDiscardDraftId, setPendingDiscardDraftId] = useState<string | null>(null);
  const [suppressedDraftAutoSelectCropIds, setSuppressedDraftAutoSelectCropIds] = useState<
    Record<string, string[]>
  >({});
  const [assignmentItemCache, setAssignmentItemCache] = useState<
    Record<string, ApiMasterLabelUPCData | null>
  >({});
  const [assignmentLoadingKeys, setAssignmentLoadingKeys] = useState<Set<string>>(() => new Set());
  const [savingDraftIds, setSavingDraftIds] = useState<Set<string>>(() => new Set());
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
  const [collectedDetectionKeys, setCollectedDetectionKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [detailsItem, setDetailsItem] = useState<DetailModalItem | null>(null);
  const [fallbackControlCamera, setFallbackControlCamera] = useState<CameraDetails | null>(null);
  const effectiveControlsMode = hasInternalAccess ? controlsMode : 'selected';
  const cameraImageMaxHeight = clamp(
    Math.floor(viewportHeight - REMOTE_DC_CAMERA_VIEWPORT_CHROME_PX),
    320,
    viewportHeight
  );

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.polygonStrokeWidth, String(polygonStrokeWidth));
  }, [polygonStrokeWidth]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.overlayOpacityPct, String(overlayOpacityPct));
  }, [overlayOpacityPct]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.imagePanePct, String(imagePanePct));
  }, [imagePanePct]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.autoImageArea, String(autoImageArea));
  }, [autoImageArea]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.workspaceZoomPct, String(workspaceZoomPct));
  }, [workspaceZoomPct]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.cropSizePx, String(cropSizePx));
  }, [cropSizePx]);

  useEffect(() => {
    if (hasInternalAccess) {
      safeLocalStorageSetItem(STORAGE_KEYS.viewControlsMode, controlsMode);
    }
  }, [controlsMode, hasInternalAccess]);

  useEffect(() => {
    safeLocalStorageSetItem(
      STORAGE_KEYS.autoCloseMasterLabelOnSave,
      String(autoCloseMasterLabelOnSave)
    );
  }, [autoCloseMasterLabelOnSave]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.autoSelectCameraCrops, String(autoSelectCameraCrops));
  }, [autoSelectCameraCrops]);

  useEffect(() => {
    let cancelled = false;
    setIsDefaultsLoading(true);
    ForgeApiService.fetchRemoteDataCollectionConfig(token, { nonAdminPreview: isNonAdminPreview })
      .then((defaults) => {
        if (cancelled) return;
        setSimilarityThresholdPct(Math.round(defaults.similarityThreshold * 100));
        setDeltaThresholdPct(Math.round(defaults.deltaThreshold * 100));
        setSsmConfidencePct(
          Math.round(
            (defaults.ssmConfidenceThreshold ??
              REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT / 100) * 100
          )
        );
        setMergeLinkedCandidates(defaults.mergeLinkedCandidates);
      })
      .catch(() => {
        if (cancelled) return;
        const defaults = fallbackRemoteDcDefaults();
        setSimilarityThresholdPct(Math.round(defaults.similarityThreshold * 100));
        setDeltaThresholdPct(Math.round(defaults.deltaThreshold * 100));
        setSsmConfidencePct(Math.round(defaults.ssmConfidenceThreshold * 100));
        setMergeLinkedCandidates(defaults.mergeLinkedCandidates);
      })
      .finally(() => {
        if (!cancelled) setIsDefaultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNonAdminPreview, token]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTextInput =
        target instanceof HTMLInputElement &&
        !['button', 'checkbox', 'radio', 'range'].includes(target.type);
      if (
        isTextInput ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return;
      if ((event.key.toLowerCase() === 'z' || event.code === 'KeyZ') && !event.repeat) {
        event.preventDefault();
        toggleZoom();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [toggleZoom]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const loadRemoteDcContext = async () => {
      if (isDefaultsLoading) {
        return;
      }
      if (!inferenceId && !imageIdParam) {
        setDetail(null);
        setPreview(null);
        setImageTimeline(null);
        setSelectedTimelineImageId(null);
        setPreviewError(null);
        setTimelineError(null);
        setCachedInferenceStatus('idle');
        setPrLoadError(null);
        setLoadError(null);
        updateSelectedDetectionIds(new Set());
        setCollectedDetectionKeys(new Set());
        setAssignmentContext(null);
        setSavingDraftIds(new Set());
        setDraftErrors({});
        setDetailsItem(null);
        setHiddenPogImageKeys(new Set());
        setIsLoading(false);
        setIsTimelineLoading(false);
        setIsPreviewLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        if (inferenceId) {
          const response = await ForgeApiService.fetchMasterDataCollectionInference(
            token,
            inferenceId,
            {
              chainId,
              itemNumber: queryItemNumber,
              normalizedBarcode: queryBarcode,
              nonAdminPreview: isNonAdminPreview,
              signal: controller.signal,
            }
          );
          if (!cancelled) {
            setDetail(response);
            setPreview(null);
            setImageTimeline(null);
            setSelectedTimelineImageId(response.imageId ?? imageIdParam ?? null);
            setLoadError(null);
            updateSelectedDetectionIds(new Set());
            setCollectedDetectionKeys(new Set());
            setAssignmentContext(null);
            setSavingDraftIds(new Set());
            setDraftErrors({});
            setDetailsItem(null);
          }
          return;
        }

        setIsTimelineLoading(true);
        setIsPreviewLoading(true);
        const previewRequest = ForgeApiService.fetchMasterDataCollectionImagePreview(
          token,
          imageIdParam as number,
          {
            chainId,
            itemNumber: queryItemNumber,
            normalizedBarcode: queryBarcode,
            signal: controller.signal,
          }
        )
          .then((response) => {
            if (cancelled) return;
            setPreview(response);
            setPreviewError(null);
            setLoadError(null);
          })
          .catch((error) => {
            if (isAbortError(error) || cancelled) return;
            setPreview(null);
            setPreviewError(
              error instanceof Error ? error.message : 'POG context could not be loaded'
            );
          })
          .finally(() => {
            if (!cancelled) setIsPreviewLoading(false);
          });
        void previewRequest;

        const timeline = await ForgeApiService.fetchMasterDataCollectionImageTimeline(
          token,
          imageIdParam as number,
          {
            chainId,
            itemNumber: queryItemNumber,
            normalizedBarcode: queryBarcode,
            previousCount: 24,
            signal: controller.signal,
          }
        );
        if (!cancelled) {
          setImageTimeline(timeline);
          setSelectedTimelineImageId(imageIdParam as number);
          setDetail(null);
          setLoadError(null);
          setTimelineError(null);
          updateSelectedDetectionIds(new Set());
          setAssignmentContext(null);
          setSavingDraftIds(new Set());
          setDraftErrors({});
          setDetailsItem(null);
        }
      } catch (error) {
        if (isAbortError(error)) return;
        if (!cancelled) {
          const message =
            error instanceof Error
              ? error.message
              : 'Error loading Master Data Collection inference';
          setLoadError(message);
          setTimelineError(message);
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsTimelineLoading(false);
        }
      }
    };
    loadRemoteDcContext();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Thresholds and merge mode are intentionally omitted after default load; they update client-side scoring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chainId,
    imageIdParam,
    inferenceId,
    isDefaultsLoading,
    queryBarcode,
    queryItemNumber,
    showErrorToast,
    token,
  ]);

  const handleImageLookupSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const imageId = imageLookupDraft.trim();
      if (!/^\d+$/.test(imageId)) {
        setImageLookupError('Enter a numeric image_id');
        return;
      }
      setImageLookupError(null);
      const params = new URLSearchParams(searchParams);
      params.set('image_id', imageId);
      params.set('load_pr', '1');
      navigate(`${FORGE_REMOTE_DC_TOOL_PATH}?${params.toString()}`);
    },
    [imageLookupDraft, navigate, searchParams]
  );

  const selectedTimelineEntry = useMemo(
    () =>
      imageTimeline?.entries.find((entry) => entry.imageId === selectedTimelineImageId) ??
      imageTimeline?.entries.find((entry) => entry.isCurrent) ??
      null,
    [imageTimeline, selectedTimelineImageId]
  );

  const timelineCameraImage = useMemo<CameraImageResponse | null>(() => {
    if (!imageTimeline || !selectedTimelineEntry?.imageUri) return null;
    return {
      chainId: imageTimeline.chainId,
      storeId: imageTimeline.storeId,
      storeName: imageTimeline.storeName,
      deviceName: imageTimeline.deviceName,
      cameraId: imageTimeline.cameraId,
      image: {
        imageId: selectedTimelineEntry.imageId,
        imageUri: selectedTimelineEntry.imageUri,
        detectedAt: selectedTimelineEntry.detectedAt || selectedTimelineEntry.createdAtUtc || '',
        templateId: 0,
        ilSectionIds: [],
      },
      pogs: {},
      templates: {},
    };
  }, [imageTimeline, selectedTimelineEntry]);

  const timelinePreviewContext = useMemo<MasterDataCollectionImagePreviewResponse | null>(() => {
    if (!imageTimeline || !selectedTimelineEntry) return null;
    return {
      chainId: imageTimeline.chainId,
      storeId: imageTimeline.storeId,
      storeName: imageTimeline.storeName,
      imageId: selectedTimelineEntry.imageId,
      imageUri: selectedTimelineEntry.imageUri,
      cameraImage: timelineCameraImage,
      requestedItemNumber: queryItemNumber,
      requestedNormalizedBarcode: queryBarcode,
    };
  }, [imageTimeline, queryBarcode, queryItemNumber, selectedTimelineEntry, timelineCameraImage]);

  const activeImageContext = detail ?? preview ?? timelinePreviewContext;
  const activeCameraImage = detail?.cameraImage ?? preview?.cameraImage ?? timelineCameraImage;
  const activeChainId = activeImageContext?.chainId ?? chainId;
  const activeImageId =
    selectedTimelineImageId ?? activeImageContext?.imageId ?? imageIdParam ?? null;
  const activeImageIdRef = useRef<number | null>(activeImageId);

  useEffect(() => {
    activeImageIdRef.current = activeImageId;
  }, [activeImageId]);

  useEffect(() => {
    if (!imageIdParam || !selectedTimelineImageId) {
      return undefined;
    }
    if (isLoading && selectedTimelineImageId === imageIdParam) {
      return undefined;
    }
    if (isPreviewLoading && selectedTimelineImageId === imageIdParam) {
      return undefined;
    }
    if (preview?.imageId === selectedTimelineImageId) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const params = new URLSearchParams();
    params.set('image_id', String(selectedTimelineImageId));
    if (activeChainId) params.set('chain', String(activeChainId));
    if (queryItemNumber) params.set('in', queryItemNumber);
    if (queryBarcode) params.set('nb', queryBarcode);
    window.history.replaceState(
      window.history.state,
      '',
      `${FORGE_REMOTE_DC_TOOL_PATH}?${params.toString()}`
    );

    setDetail(null);
    setPreview(null);
    setPreviewError(null);
    setCachedInferenceStatus('idle');
    setPrLoadError(null);
    updateSelectedDetectionIds(new Set());
    setAssignmentContext(null);
    setDetailsItem(null);
    setHiddenPogImageKeys(new Set());
    setIsPreviewLoading(true);

    ForgeApiService.fetchMasterDataCollectionImagePreview(token, selectedTimelineImageId, {
      chainId: activeChainId,
      itemNumber: queryItemNumber,
      normalizedBarcode: queryBarcode,
      signal: controller.signal,
    })
      .then((response) => {
        if (cancelled) return;
        setPreview(response);
        setPreviewError(null);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        if (cancelled) return;
        setPreview(null);
        setPreviewError(error instanceof Error ? error.message : 'POG context could not be loaded');
      })
      .finally(() => {
        if (!cancelled) setIsPreviewLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    activeChainId,
    imageIdParam,
    isLoading,
    isPreviewLoading,
    preview?.imageId,
    queryBarcode,
    queryItemNumber,
    selectedTimelineImageId,
    token,
    updateSelectedDetectionIds,
  ]);

  const cameraTimeline = useMemo<CameraImageTimelineResponse | null>(() => {
    const timelineImages =
      imageTimeline?.entries
        .filter((entry) => entry.imageUri)
        .map((entry) => ({
          imageId: entry.imageId,
          imageUri: entry.imageUri as string,
          detectedAt: entry.detectedAt || entry.createdAtUtc || '',
          templateId:
            entry.imageId === activeCameraImage?.image.imageId
              ? activeCameraImage.image.templateId
              : 0,
          ilSectionIds:
            entry.imageId === activeCameraImage?.image.imageId
              ? activeCameraImage.image.ilSectionIds
              : [],
        })) ?? [];
    if (!activeCameraImage && timelineImages.length === 0) return null;
    if (timelineImages.length > 0) {
      return {
        images: timelineImages,
        templates: activeCameraImage?.templates ?? {},
        pogs: activeCameraImage?.pogs ?? {},
      };
    }
    if (!activeCameraImage) return null;
    return {
      images: [activeCameraImage.image],
      templates: activeCameraImage.templates,
      pogs: activeCameraImage.pogs,
    };
  }, [activeCameraImage, imageTimeline]);

  const {
    preloadedImages,
    preloadedImagesB64,
    isLoading: isLoadingImages,
  } = useImagePreloader({
    cameraTimeline,
    authToken: token,
    priorityImageId: activeImageId,
  });

  const currentImage = activeCameraImage?.image ?? null;
  const preloadedImage = currentImage ? preloadedImagesB64[currentImage.imageUri] : undefined;
  const cameraImageElement = currentImage ? preloadedImages[currentImage.imageUri] : undefined;
  const cameraImageSrc = preloadedImage || currentImage?.imageUri;
  const cameraCropSource = preloadedImage?.startsWith('data:') ? preloadedImage : undefined;
  const cameraImageAspectRatio = getImageAspectRatio(cameraImageElement);
  const autoCameraPaneWidth = Math.ceil(cameraImageAspectRatio * cameraImageMaxHeight);
  const imageAreaCssValue = autoImageArea
    ? rightPaneMode === 'master-label'
      ? '50%'
      : `${autoCameraPaneWidth}px`
    : `${imagePanePct}%`;

  useEffect(() => {
    handleImageLeave();
  }, [cameraImageMaxHeight, handleImageLeave, imageAreaCssValue]);

  useEffect(() => {
    if (!imageIdParam || !activeImageId || detail) return undefined;
    if (!cameraImageElement && !cameraImageSrc) return undefined;

    let cancelled = false;
    const controller = new AbortController();
    setCachedInferenceStatus('checking');
    ForgeApiService.loadMasterDataCollectionImageInference(token, activeImageId, {
      chainId: activeChainId,
      itemNumber: queryItemNumber,
      normalizedBarcode: queryBarcode,
      nonAdminPreview: isNonAdminPreview,
      cachedOnly: true,
      signal: controller.signal,
    })
      .then((response) => {
        if (cancelled) return;
        setDetail(response);
        setCachedInferenceStatus('hit');
        updateSelectedDetectionIds(new Set());
        setAssignmentContext(null);
        setDetailsItem(null);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        if (cancelled) return;
        const message = error instanceof Error ? error.message : '';
        if (
          (error instanceof Error &&
            'status' in error &&
            (error as { status?: number }).status === 404) ||
          message.includes('Cached product recognition inference not found')
        ) {
          setCachedInferenceStatus('miss');
        } else {
          setCachedInferenceStatus('error');
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    activeChainId,
    activeImageId,
    cameraImageSrc,
    cameraImageElement,
    detail,
    imageIdParam,
    isNonAdminPreview,
    queryBarcode,
    queryItemNumber,
    token,
    updateSelectedDetectionIds,
  ]);

  const controlStoreName = activeCameraImage?.storeName ?? activeImageContext?.storeName ?? null;
  const controlCameraId = activeCameraImage?.cameraId ?? null;
  const controlCamera =
    fallbackControlCamera?.cameraId === controlCameraId ? fallbackControlCamera : null;
  const controlDeviceName = activeCameraImage?.deviceName ?? controlCamera?.deviceName ?? null;
  const controlModTagNames = useMemo(
    () => (controlCamera?.mods ?? []).map((mod) => mod.tagName),
    [controlCamera]
  );
  const currentPogs = useMemo(
    () =>
      orderRemoteDcPogs(
        activeCameraImage?.pogs ? Object.values(activeCameraImage.pogs) : [],
        controlModTagNames,
        activeCameraImage?.image.ilSectionIds ?? []
      ),
    [activeCameraImage, controlModTagNames]
  );
  const controlLink = useMemo(
    () => controlImageUrl(controlStoreName, controlDeviceName, activeImageId),
    [activeImageId, controlDeviceName, controlStoreName]
  );

  useEffect(() => {
    if (!token || !controlStoreName || !controlCameraId) {
      setFallbackControlCamera(null);
      return undefined;
    }

    let cancelled = false;
    setFallbackControlCamera(null);
    void ApiService.fetchCameras(token, controlStoreName, { useCache: true })
      .then((cameras) => {
        if (cancelled) return;
        const camera = cameras.find((candidate) => candidate.cameraId === controlCameraId);
        setFallbackControlCamera(camera ?? null);
      })
      .catch(() => {
        if (!cancelled) setFallbackControlCamera(null);
      });

    return () => {
      cancelled = true;
    };
  }, [controlCameraId, controlStoreName, token]);

  useEffect(() => {
    setHiddenPogImageKeys(new Set());
  }, [activeImageId]);

  const selectedIlItemIds = useMemo(
    () =>
      new Set(
        [
          assignmentContext?.pog?.ilItemId,
          ...assignmentDrafts.map((draft) => draft.context.pog?.ilItemId),
        ].filter((ilItemId): ilItemId is number => Boolean(ilItemId))
      ),
    [assignmentContext, assignmentDrafts]
  );

  const similarityThreshold = similarityThresholdPct / 100;
  const deltaThreshold = deltaThresholdPct / 100;
  const ssmConfidenceThreshold = ssmConfidencePct / 100;
  const overlayOpacity = overlayOpacityPct / 100;

  const scoredDetections = useMemo(
    () =>
      (detail?.detections ?? []).map((detection) =>
        rescoreDetectionForMergeLinkedCandidates(
          detection,
          mergeLinkedCandidates,
          similarityThreshold,
          deltaThreshold
        )
      ),
    [deltaThreshold, detail?.detections, mergeLinkedCandidates, similarityThreshold]
  );

  const visibleDetections = useMemo(() => {
    return scoredDetections.filter(
      (detection) =>
        (visibleDetectionType === 'both' ||
          detection.classificationType === visibleDetectionType) &&
        (!useSsmConfidenceThreshold ||
          detectionPassesSsmConfidenceThreshold(detection, ssmConfidenceThreshold))
    );
  }, [scoredDetections, ssmConfidenceThreshold, useSsmConfidenceThreshold, visibleDetectionType]);

  const highlightedDetections = useMemo(
    () =>
      visibleDetections.filter((detection) =>
        detectionMatchesThresholds(
          detection,
          similarityThreshold,
          deltaThreshold,
          useSimilarityThreshold,
          useDeltaThreshold
        )
      ),
    [
      deltaThreshold,
      similarityThreshold,
      useDeltaThreshold,
      useSimilarityThreshold,
      visibleDetections,
    ]
  );

  const highlightedDetectionIds = useMemo(
    () => new Set(highlightedDetections.map((detection) => detection.detectionId)),
    [highlightedDetections]
  );

  const preparedDetectionIds = useMemo(
    () => new Set(assignmentDrafts.flatMap((draft) => draft.detectionIds)),
    [assignmentDrafts]
  );

  const savingDetectionIds = useMemo(
    () =>
      new Set(
        assignmentDrafts
          .filter((draft) => savingDraftIds.has(draft.id))
          .flatMap((draft) => draft.detectionIds)
      ),
    [assignmentDrafts, savingDraftIds]
  );

  const queuedDetectionIds = useMemo(
    () => new Set([...selectedDetectionIds, ...preparedDetectionIds]),
    [preparedDetectionIds, selectedDetectionIds]
  );

  const displayedDetections = useMemo(
    () =>
      visibleDetections.filter((detection) => {
        const isCollected = collectedDetectionKeys.has(detectionCollectionKey(detection));
        return (
          highlightedDetectionIds.has(detection.detectionId) ||
          queuedDetectionIds.has(detection.detectionId) ||
          isCollected
        );
      }),
    [collectedDetectionKeys, highlightedDetectionIds, queuedDetectionIds, visibleDetections]
  );

  const productRecognitionDetections = useMemo(
    () =>
      orderDetectionsForOverlay(displayedDetections, visibleDetectionType).map((detection) =>
        toImageBox(
          detection,
          similarityThreshold,
          deltaThreshold,
          useSimilarityThreshold,
          useDeltaThreshold,
          queuedDetectionIds,
          collectedDetectionKeys,
          polygonStrokeWidth,
          overlayOpacity
        )
      ),
    [
      deltaThreshold,
      overlayOpacity,
      polygonStrokeWidth,
      collectedDetectionKeys,
      queuedDetectionIds,
      similarityThreshold,
      useDeltaThreshold,
      useSimilarityThreshold,
      displayedDetections,
      visibleDetectionType,
    ]
  );

  const selectedDetections = useMemo(
    () => scoredDetections.filter((detection) => selectedDetectionIds.has(detection.detectionId)),
    [scoredDetections, selectedDetectionIds]
  );

  const loadAssignmentItem = useCallback(
    async (context: AssignmentContext, options?: { force?: boolean }) => {
      const key = assignmentCacheKey(activeChainId, context);
      if (!key || !activeChainId) return;
      if (!options?.force && Object.prototype.hasOwnProperty.call(assignmentItemCache, key)) return;
      setAssignmentLoadingKeys((current) => new Set(current).add(key));
      try {
        let item: ApiMasterLabelUPCData | null = null;
        if (context.itemNumber) {
          item = await ForgeApiService.fetchItem(token, String(context.itemNumber), activeChainId);
        } else if (context.normalizedBarcode) {
          const response = await ForgeApiService.fetchItemByBarcode(
            token,
            String(context.normalizedBarcode),
            activeChainId
          );
          item = response.item;
        }
        setAssignmentItemCache((current) => ({ ...current, [key]: item }));
      } catch (error) {
        setAssignmentItemCache((current) => ({ ...current, [key]: null }));
        showErrorToast(
          error instanceof Error ? error.message : 'Error loading Master Label target'
        );
      } finally {
        setAssignmentLoadingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [activeChainId, assignmentItemCache, showErrorToast, token]
  );

  const openAssignmentContext = useCallback(
    (context: AssignmentContext) => {
      setAssignmentContext(context);
      void loadAssignmentItem(context);
    },
    [loadAssignmentItem]
  );

  const ensureAssignmentDraft = useCallback(
    (context: AssignmentContext) => {
      const draftImageId = detail?.imageId ?? activeImageId;
      const draftId = assignmentDraftId(context, draftImageId);
      setAssignmentDrafts((current) =>
        current.some((draft) => draft.id === draftId)
          ? current
          : [
              ...current,
              {
                id: draftId,
                chainId: activeChainId ?? chainId ?? 0,
                imageId: draftImageId,
                inferenceId: detail?.inferenceId ?? null,
                context,
                detectionIds: [],
                detections: [],
                cropImages: {},
                createdAtMs: Date.now(),
              },
            ]
      );
      setFocusedDraftId(draftId);
      return draftId;
    },
    [activeChainId, activeImageId, chainId, detail?.imageId, detail?.inferenceId]
  );

  const refreshAssignmentItem = useCallback(
    async (context: AssignmentContext) => {
      await loadAssignmentItem(context, { force: true });
    },
    [loadAssignmentItem]
  );

  const detectionsById = useMemo(
    () => new Map(scoredDetections.map((detection) => [detection.detectionId, detection])),
    [scoredDetections]
  );

  const prepareAssignmentDraft = useCallback(
    (context: AssignmentContext | null, detectionIds: string[]) => {
      if (!context) {
        showErrorToast('Choose a target first');
        return;
      }
      const uniqueDetectionIds = [...new Set(detectionIds)];
      if (uniqueDetectionIds.length === 0) {
        showErrorToast('Select at least one polygon first');
        return;
      }
      if (!activeChainId) {
        showErrorToast('Remote DC image context is not loaded yet');
        return;
      }
      const draftDetections = uniqueDetectionIds
        .map((detectionId) => detectionsById.get(detectionId))
        .filter((detection): detection is RemoteDataCollectionDetection => Boolean(detection));
      if (draftDetections.length === 0) {
        showErrorToast('Prepared crop detections are not available');
        return;
      }
      const draftImageId = detail?.imageId ?? activeImageId;
      const draftId = assignmentDraftId(context, draftImageId);
      const cropImages = Object.fromEntries(
        draftDetections.map((detection) => [
          detection.detectionId,
          cropImageFromDetection(detection, cameraImageElement, ''),
        ])
      );
      setAssignmentDrafts((current) => {
        const existing = current.find((draft) => draft.id === draftId);
        if (!existing) {
          return [
            ...current,
            {
              id: draftId,
              chainId: activeChainId,
              imageId: draftImageId,
              inferenceId: detail?.inferenceId ?? null,
              context,
              detectionIds: draftDetections.map((detection) => detection.detectionId),
              detections: draftDetections,
              cropImages,
              createdAtMs: Date.now(),
            },
          ];
        }
        const detectionsByDraftId = new Map(
          existing.detections.map((detection) => [detection.detectionId, detection])
        );
        draftDetections.forEach((detection) =>
          detectionsByDraftId.set(detection.detectionId, detection)
        );
        const mergedCropImages = { ...existing.cropImages, ...cropImages };
        const mergedDetections = [...detectionsByDraftId.values()];
        return current.map((draft) =>
          draft.id === draftId
            ? {
                ...draft,
                chainId: activeChainId,
                imageId: draftImageId,
                inferenceId: detail?.inferenceId ?? draft.inferenceId,
                context,
                detectionIds: mergedDetections.map((detection) => detection.detectionId),
                detections: mergedDetections,
                cropImages: mergedCropImages,
              }
            : draft
        );
      });
      setAssignmentContext(context);
      setFocusedDraftId(draftId);
      setRightPaneMode('master-label');
      setDraftErrors((current) => {
        const next = { ...current };
        delete next[draftId];
        return next;
      });
      updateSelectedDetectionIds(new Set());
      void loadAssignmentItem(context);
    },
    [
      activeChainId,
      activeImageId,
      cameraImageElement,
      detail?.imageId,
      detail?.inferenceId,
      detectionsById,
      loadAssignmentItem,
      showErrorToast,
      updateSelectedDetectionIds,
    ]
  );

  const handlePrepareCurrent = useCallback(() => {
    const selectedIds =
      selectedDetectionIdsRef.current.size > 0
        ? selectedDetectionIdsRef.current
        : selectedDetectionIds;
    prepareAssignmentDraft(assignmentContext, [...selectedIds]);
  }, [assignmentContext, prepareAssignmentDraft, selectedDetectionIds]);

  const suppressDraftAutoSelectCrops = useCallback((draftId: string, cropIds: string[]) => {
    if (cropIds.length === 0) return;
    setSuppressedDraftAutoSelectCropIds((current) => {
      const merged = new Set([...(current[draftId] ?? []), ...cropIds]);
      return { ...current, [draftId]: [...merged] };
    });
  }, []);

  const allowDraftAutoSelectCrops = useCallback((draftId: string, cropIds: string[]) => {
    if (cropIds.length === 0) return;
    const removedCropIds = new Set(cropIds);
    setSuppressedDraftAutoSelectCropIds((current) => {
      const remaining = (current[draftId] ?? []).filter((cropId) => !removedCropIds.has(cropId));
      if (remaining.length === (current[draftId] ?? []).length) return current;
      const next = { ...current };
      if (remaining.length > 0) next[draftId] = remaining;
      else delete next[draftId];
      return next;
    });
  }, []);

  const handleRemoveDetectionsFromDraft = useCallback(
    (draftId: string, detectionIds: string[]) => {
      if (detectionIds.length === 0) return;
      const removedDetectionIds = new Set(detectionIds);
      allowDraftAutoSelectCrops(draftId, detectionIds.map(remoteTempCropId));
      setAssignmentDrafts((current) =>
        removeDetectionsFromAssignmentDraft(current, draftId, removedDetectionIds)
      );
      updateSelectedDetectionIds((current) => {
        const next = new Set(current);
        removedDetectionIds.forEach((detectionId) => next.delete(detectionId));
        return next;
      });
      setDraftErrors((current) => {
        if (!current[draftId]) return current;
        const next = { ...current };
        delete next[draftId];
        return next;
      });
    },
    [allowDraftAutoSelectCrops, updateSelectedDetectionIds]
  );

  const handleSubmitPreparedDraft = useCallback(
    async (draftId: string, payload: RemoteDCMasterLabelSavePayload) => {
      const draft = assignmentDrafts.find((candidateDraft) => candidateDraft.id === draftId);
      if (!draft || !detail) return;
      if (payload.state.items.length === 0) {
        showErrorToast('This Master Label draft has no changes to save');
        return;
      }
      setSavingDraftIds((current) => new Set(current).add(draftId));
      setDraftErrors((current) => {
        const next = { ...current };
        delete next[draftId];
        return next;
      });
      if (autoCloseMasterLabelOnSave) {
        setRightPaneMode('pog');
        setFocusedDraftId(null);
      }
      const savedDetectionIds = new Set<string>();
      try {
        const response = await ForgeApiService.saveMasterDataCollectionMasterLabelState(
          token,
          draft.inferenceId || detail.inferenceId,
          {
            state: payload.state,
            remote_crops: payload.remoteCrops,
          }
        );
        payload.submittedDetectionIds.forEach((detectionId) => savedDetectionIds.add(detectionId));
        const createdCount = Object.keys(response.createdRemoteCrops).length;
        const draftDetectionsById = new Map(
          draft.detections.map((detection) => [detection.detectionId, detection])
        );
        if (savedDetectionIds.size > 0) {
          setCollectedDetectionKeys((current) => {
            const next = new Set(current);
            savedDetectionIds.forEach((detectionId) => {
              const detection =
                detectionsById.get(detectionId) || draftDetectionsById.get(detectionId);
              if (detection) next.add(detectionCollectionKey(detection));
            });
            return next;
          });
          updateSelectedDetectionIds((current) => {
            const next = new Set(current);
            savedDetectionIds.forEach((detectionId) => next.delete(detectionId));
            return next;
          });
          setAssignmentDrafts((current) =>
            autoCloseMasterLabelOnSave
              ? removeSavedDetectionsFromDrafts(current, draftId, savedDetectionIds)
              : current.map((candidateDraft) =>
                  candidateDraft.id === draftId
                    ? {
                        ...candidateDraft,
                        detectionIds: candidateDraft.detectionIds.filter(
                          (detectionId) => !savedDetectionIds.has(detectionId)
                        ),
                        detections: candidateDraft.detections.filter(
                          (detection) => !savedDetectionIds.has(detection.detectionId)
                        ),
                        cropImages: Object.fromEntries(
                          Object.entries(candidateDraft.cropImages).filter(
                            ([detectionId]) => !savedDetectionIds.has(detectionId)
                          )
                        ),
                      }
                    : candidateDraft
                )
          );
          allowDraftAutoSelectCrops(draftId, [...savedDetectionIds].map(remoteTempCropId));
        }
        if (!autoCloseMasterLabelOnSave) {
          setRightPaneMode('master-label');
          setFocusedDraftId(draftId);
        }
        const savedCount = response.state.summary
          ? Object.values(response.state.summary).reduce(
              (total, value) => total + (typeof value === 'number' ? value : 0),
              0
            )
          : 0;
        const suffix =
          createdCount > 0
            ? ` and created ${createdCount} crop${createdCount === 1 ? '' : 's'}`
            : '';
        showSuccessToast(
          `Saved Master Label changes${savedCount ? ` (${savedCount})` : ''}${suffix}`
        );
        if (autoCloseMasterLabelOnSave) {
          void refreshAssignmentItem(draft.context).catch(() => {
            showErrorToast('Saved changes, but failed to refresh Master Label data');
          });
        } else {
          try {
            await refreshAssignmentItem(draft.context);
          } catch {
            showErrorToast('Saved changes, but failed to refresh Master Label data');
          }
        }
      } catch (error) {
        const message =
          error instanceof ForgeApiError && error.status === 409
            ? error.detail || 'Master Label changed, review placement and save again.'
            : error instanceof Error
              ? error.message
              : 'Error saving Master Label changes';
        setDraftErrors((current) => ({ ...current, [draftId]: message }));
        setRightPaneMode('master-label');
        setFocusedDraftId(draftId);
        if (error instanceof ForgeApiError && error.status === 409) {
          void refreshAssignmentItem(draft.context);
        }
        showErrorToast(message);
      } finally {
        setSavingDraftIds((current) => {
          const next = new Set(current);
          next.delete(draftId);
          return next;
        });
      }
    },
    [
      assignmentDrafts,
      allowDraftAutoSelectCrops,
      autoCloseMasterLabelOnSave,
      detail,
      detectionsById,
      refreshAssignmentItem,
      showErrorToast,
      showSuccessToast,
      token,
      updateSelectedDetectionIds,
    ]
  );

  useEffect(() => {
    if (assignmentDrafts.length === 0) {
      setRightPaneMode('pog');
      setFocusedDraftId(null);
      return;
    }
    if (focusedDraftId && assignmentDrafts.some((draft) => draft.id === focusedDraftId)) return;
    setFocusedDraftId(assignmentDrafts[0].id);
  }, [assignmentDrafts, focusedDraftId]);

  const handleToggleDetectionSelection = useCallback(
    (detection: RemoteDataCollectionDetection) => {
      const isCollected = collectedDetectionKeys.has(detectionCollectionKey(detection));
      if (isCollected || preparedDetectionIds.has(detection.detectionId)) return;
      updateSelectedDetectionIds((current) => {
        const next = new Set(current);
        if (next.has(detection.detectionId)) next.delete(detection.detectionId);
        else next.add(detection.detectionId);
        return next;
      });
    },
    [collectedDetectionKeys, preparedDetectionIds, updateSelectedDetectionIds]
  );

  const loadDetectionIntoActiveDraft = useCallback(
    (detection: RemoteDataCollectionDetection): boolean => {
      if (rightPaneMode !== 'master-label' || !focusedDraftId) return false;
      const activeDraft = assignmentDrafts.find((draft) => draft.id === focusedDraftId);
      if (!activeDraft) return false;
      const isCollected = collectedDetectionKeys.has(detectionCollectionKey(detection));
      const isLoadedInAnotherDraft = assignmentDrafts.some(
        (draft) => draft.id !== activeDraft.id && draft.detectionIds.includes(detection.detectionId)
      );
      if (isCollected || savingDetectionIds.has(detection.detectionId) || isLoadedInAnotherDraft) {
        return true;
      }
      const cropImage = cropImageFromDetection(detection, cameraImageElement, '');
      allowDraftAutoSelectCrops(activeDraft.id, [remoteTempCropId(detection.detectionId)]);
      setAssignmentDrafts((current) =>
        toggleDetectionInActiveAssignmentDraft(current, activeDraft.id, detection, cropImage)
      );
      updateSelectedDetectionIds((current) => {
        if (!current.has(detection.detectionId)) return current;
        const next = new Set(current);
        next.delete(detection.detectionId);
        return next;
      });
      setDraftErrors((current) => {
        if (!current[activeDraft.id]) return current;
        const next = { ...current };
        delete next[activeDraft.id];
        return next;
      });
      return true;
    },
    [
      assignmentDrafts,
      cameraImageElement,
      collectedDetectionKeys,
      focusedDraftId,
      allowDraftAutoSelectCrops,
      rightPaneMode,
      savingDetectionIds,
      updateSelectedDetectionIds,
    ]
  );

  const handleDetectionClick = (box: ImageBox) => {
    const detection = box.item as unknown as RemoteDataCollectionDetection;
    if (!detection?.detectionId) return;
    if (loadDetectionIntoActiveDraft(detection)) return;
    handleToggleDetectionSelection(detection);
  };

  const handleOpenPogMasterLabel = useCallback(
    (bbox: PogBBox) => {
      setDetailsItem(null);
      const context = pogAssignmentContext(bbox);
      openAssignmentContext(context);
      setRightPaneMode('master-label');
      const selectedIds =
        selectedDetectionIdsRef.current.size > 0
          ? selectedDetectionIdsRef.current
          : selectedDetectionIds;
      if (selectedIds.size > 0) {
        prepareAssignmentDraft(context, [...selectedIds]);
        return;
      }
      ensureAssignmentDraft(context);
    },
    [ensureAssignmentDraft, openAssignmentContext, prepareAssignmentDraft, selectedDetectionIds]
  );

  const handlePogBoxClick = useCallback((bbox: PogBBox) => {
    setDetailsItem({ kind: 'pog', bbox });
  }, []);

  const handleAssignCandidate = (
    detection: RemoteDataCollectionDetection,
    target: AssignmentContext
  ) => {
    setDetailsItem(null);
    prepareAssignmentDraft(target, [...selectedDetectionIds, detection.detectionId]);
  };

  const handleDetectionRightClick = useCallback((box: PogBBox | ImageBox) => {
    const detection = (box as ImageBox).item as RemoteDataCollectionDetection | undefined;
    if (!detection?.detectionId) return;
    setDetailsItem({ kind: 'detection', detection });
  }, []);

  const handlePogBoxRightClick = useCallback(
    (bbox: PogBBox) => {
      handleOpenPogMasterLabel(bbox);
    },
    [handleOpenPogMasterLabel]
  );

  const handleTogglePogImage = useCallback((pogKey: string) => {
    setHiddenPogImageKeys((current) => {
      const next = new Set(current);
      if (next.has(pogKey)) next.delete(pogKey);
      else next.add(pogKey);
      return next;
    });
  }, []);

  const handleLoadImageInference = useCallback(async () => {
    if (!activeImageId) return;
    const requestImageId = activeImageId;
    setIsLoadingInferenceForImage(true);
    setPrLoadError(null);
    try {
      const response = await ForgeApiService.loadMasterDataCollectionImageInference(
        token,
        requestImageId,
        {
          chainId: activeChainId,
          itemNumber: queryItemNumber,
          normalizedBarcode: queryBarcode,
          nonAdminPreview: isNonAdminPreview,
        }
      );
      if (requestImageId !== activeImageIdRef.current) return;
      setDetail(response);
      setCachedInferenceStatus('hit');
      updateSelectedDetectionIds(new Set());
      setAssignmentContext(null);
    } catch (error) {
      if (requestImageId !== activeImageIdRef.current) return;
      const message = error instanceof Error ? error.message : 'Error loading product recognition';
      setPrLoadError(message);
      setCachedInferenceStatus('error');
      showErrorToast(message);
    } finally {
      if (requestImageId === activeImageIdRef.current) setIsLoadingInferenceForImage(false);
    }
  }, [
    activeChainId,
    activeImageId,
    isNonAdminPreview,
    queryBarcode,
    queryItemNumber,
    showErrorToast,
    token,
    updateSelectedDetectionIds,
  ]);

  useEffect(() => {
    if (
      !shouldAutoLoadImageInference ||
      !activeImageId ||
      detail ||
      isLoadingInferenceForImage ||
      !['miss', 'error'].includes(cachedInferenceStatus)
    ) {
      return;
    }
    if (!cameraImageElement && !cameraImageSrc) return;

    const autoLoadKey = [
      activeImageId,
      activeChainId ?? '',
      queryItemNumber ?? '',
      queryBarcode ?? '',
    ].join(':');
    if (autoLoadImageInferenceKeyRef.current === autoLoadKey) return;
    autoLoadImageInferenceKeyRef.current = autoLoadKey;
    void handleLoadImageInference();
  }, [
    activeChainId,
    activeImageId,
    cachedInferenceStatus,
    cameraImageElement,
    cameraImageSrc,
    detail,
    handleLoadImageInference,
    isLoadingInferenceForImage,
    queryBarcode,
    queryItemNumber,
    shouldAutoLoadImageInference,
  ]);

  const timelineEntries = useMemo(() => imageTimeline?.entries ?? [], [imageTimeline]);
  const selectedTimelineIndex = Math.max(
    0,
    timelineEntries.findIndex((entry) => entry.imageId === activeImageId)
  );
  const selectedTimelineForLabel = timelineEntries[selectedTimelineIndex] ?? selectedTimelineEntry;
  const timelineTimeLabel = selectedTimelineForLabel
    ? formatDailyNavigationTime(
        selectedTimelineForLabel.detectedAt || selectedTimelineForLabel.createdAtUtc
      )
    : '';
  const shouldShowImageTimeline = Boolean(imageIdParam) && !inferenceId;
  const focusedDraft = useMemo(
    () => assignmentDrafts.find((draft) => draft.id === focusedDraftId) ?? assignmentDrafts[0],
    [assignmentDrafts, focusedDraftId]
  );
  const focusedDraftItem = useMemo(() => {
    if (!focusedDraft) return null;
    const key = assignmentCacheKey(focusedDraft.chainId, focusedDraft.context);
    return key ? assignmentItemCache[key] : null;
  }, [assignmentItemCache, focusedDraft]);
  const focusedDraftIsLoading = useMemo(() => {
    if (!focusedDraft) return false;
    const key = assignmentCacheKey(focusedDraft.chainId, focusedDraft.context);
    return Boolean(key && assignmentLoadingKeys.has(key));
  }, [assignmentLoadingKeys, focusedDraft]);
  const focusedDraftDetections = focusedDraft?.detections ?? [];
  const pendingDiscardDraft = useMemo(
    () =>
      pendingDiscardDraftId
        ? (assignmentDrafts.find((draft) => draft.id === pendingDiscardDraftId) ?? null)
        : null,
    [assignmentDrafts, pendingDiscardDraftId]
  );

  const keepEditingPendingDiscardDraft = useCallback(() => {
    setPendingDiscardDraftId(null);
  }, []);

  const handleDraftDirtyChange = useCallback((draftId: string, hasChanges: boolean) => {
    setDirtyDraftIds((current) => {
      const alreadyDirty = current.has(draftId);
      if (alreadyDirty === hasChanges) return current;
      const next = new Set(current);
      if (hasChanges) next.add(draftId);
      else next.delete(draftId);
      return next;
    });
  }, []);

  const discardAssignmentDraft = useCallback(
    (draftId: string) => {
      const draft = assignmentDrafts.find((candidateDraft) => candidateDraft.id === draftId);
      if (draft) {
        allowDraftAutoSelectCrops(draftId, draft.detectionIds.map(remoteTempCropId));
      }
      setAssignmentDrafts((current) =>
        current.filter((candidateDraft) => candidateDraft.id !== draftId)
      );
      setFocusedDraftId((current) => (current === draftId ? null : current));
      setPendingDiscardDraftId((current) => (current === draftId ? null : current));
      setDirtyDraftIds((current) => {
        if (!current.has(draftId)) return current;
        const next = new Set(current);
        next.delete(draftId);
        return next;
      });
      setDraftErrors((current) => {
        if (!current[draftId]) return current;
        const next = { ...current };
        delete next[draftId];
        return next;
      });
      setRightPaneMode('pog');
    },
    [allowDraftAutoSelectCrops, assignmentDrafts]
  );

  const confirmPendingDiscardDraft = useCallback(() => {
    if (!pendingDiscardDraftId) return;
    discardAssignmentDraft(pendingDiscardDraftId);
  }, [discardAssignmentDraft, pendingDiscardDraftId]);

  const requestCloseFocusedDraft = useCallback(() => {
    if (!focusedDraft) {
      setRightPaneMode('pog');
      return;
    }
    if (savingDraftIds.has(focusedDraft.id)) {
      showErrorToast('Wait for this Master Label save to finish before closing it');
      return;
    }
    if (
      masterLabelDraftNeedsDiscardConfirmation(focusedDraft, dirtyDraftIds.has(focusedDraft.id))
    ) {
      setPendingDiscardDraftId(focusedDraft.id);
      return;
    }
    discardAssignmentDraft(focusedDraft.id);
  }, [dirtyDraftIds, discardAssignmentDraft, focusedDraft, savingDraftIds, showErrorToast]);

  useEffect(() => {
    if (!pendingDiscardDraftId) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        keepEditingPendingDiscardDraft();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        confirmPendingDiscardDraft();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirmPendingDiscardDraft, keepEditingPendingDiscardDraft, pendingDiscardDraftId]);

  const handleTimelineIndexChange = useCallback(
    (nextIndex: number) => {
      if (timelineEntries.length === 0) return;
      const boundedIndex = clamp(nextIndex, 0, timelineEntries.length - 1);
      const nextEntry = timelineEntries[boundedIndex];
      if (nextEntry) setSelectedTimelineImageId(nextEntry.imageId);
    },
    [timelineEntries]
  );

  useEffect(() => {
    if (rightPaneMode !== 'pog' || timelineEntries.length <= 1) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      )
        return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handleTimelineIndexChange(selectedTimelineIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleTimelineIndexChange(selectedTimelineIndex + 1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleTimelineIndexChange, rightPaneMode, selectedTimelineIndex, timelineEntries.length]);

  const showSelectedControls = effectiveControlsMode !== 'none';
  const showAdvancedControls = hasInternalAccess && effectiveControlsMode === 'all';

  return (
    <div
      className="page-content page-content--wide master-data-collection-page"
      style={{ '--mdc-ui-scale': workspaceZoomPct / 100 } as React.CSSProperties}
    >
      <div className="forge-card mdc-page-hero">
        <div className="mdc-page-hero-bar">
          <h1>Remote Data Collection</h1>
          <div className="mdc-page-hero-actions">
            {hasInternalAccess ? (
              controlLink ? (
                <a href={controlLink} target="_blank" rel="noreferrer" className="mdc-control-link">
                  Open in Control <OpenInNewOutlinedIcon style={{ fontSize: 15 }} />
                </a>
              ) : (
                <span className="mdc-control-link mdc-control-link--disabled" aria-disabled="true">
                  Open in Control <OpenInNewOutlinedIcon style={{ fontSize: 15 }} />
                </span>
              )
            ) : null}
            <button
              type="button"
              className={`mdc-controls-toggle${zoomEnabled ? ' is-active' : ''}`}
              aria-pressed={zoomEnabled}
              aria-label={zoomEnabled ? 'Zoom on hover (ON)' : 'Zoom on hover (OFF - hold Alt)'}
              title="Toggle crop zoom (Z)"
              onClick={toggleZoom}
            >
              <ZoomInIcon style={{ fontSize: 13 }} />
              Zoom
            </button>
            {hasInternalAccess ? (
              <SegmentedControl
                value={controlsMode}
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'selected', label: 'Selected' },
                  { value: 'all', label: 'All' },
                ]}
                onChange={setControlsMode}
                ariaLabel="Remote Data Collection controls visibility"
                className="mdc-controls-mode-tabs"
              />
            ) : null}
          </div>
        </div>

        {showSelectedControls ? (
          <div className="mdc-controls-panel">
            <section className="mdc-control-section mdc-control-section--primary">
              <div className="mdc-control-grid mdc-control-grid--primary">
                <div className={`mdc-range-control${autoImageArea ? ' is-disabled' : ''}`}>
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Image size</span>
                      <span className="mdc-range-label-short">Image</span>
                    </span>
                    <span className="mdc-value-pill">
                      {autoImageArea ? 'Auto' : `${imagePanePct}%`}
                    </span>
                  </div>
                  <input
                    aria-label="Image area"
                    className="mdc-range-input"
                    type="range"
                    min={IMAGE_PANE_RANGE.min}
                    max={IMAGE_PANE_RANGE.max}
                    value={imagePanePct}
                    disabled={autoImageArea}
                    onChange={(event) => setImagePanePct(Number(event.target.value))}
                  />
                </div>
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">Text size</span>
                    <span className="mdc-value-pill">{workspaceZoomPct}%</span>
                  </div>
                  <input
                    aria-label="Text size"
                    className="mdc-range-input"
                    type="range"
                    min={WORKSPACE_ZOOM_RANGE.min}
                    max={WORKSPACE_ZOOM_RANGE.max}
                    value={workspaceZoomPct}
                    onChange={(event) => setWorkspaceZoomPct(Number(event.target.value))}
                  />
                </div>
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Crop size</span>
                      <span className="mdc-range-label-short">Crop</span>
                    </span>
                    <span className="mdc-value-pill">{cropSizePx}px</span>
                  </div>
                  <input
                    aria-label="Crop size"
                    className="mdc-range-input"
                    type="range"
                    min={MASTER_LABEL_IMAGE_SLIDER_MIN}
                    max={MASTER_LABEL_IMAGE_SLIDER_MAX}
                    value={cropSizePx}
                    onChange={(event) => setCropSizePx(Number(event.target.value))}
                  />
                </div>
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Border thickness</span>
                      <span className="mdc-range-label-short">Border</span>
                    </span>
                    <span className="mdc-value-pill">{polygonStrokeWidth}px</span>
                  </div>
                  <input
                    aria-label="Polygon border"
                    className="mdc-range-input"
                    type="range"
                    min={POLYGON_STROKE_RANGE.min}
                    max={POLYGON_STROKE_RANGE.max}
                    value={polygonStrokeWidth}
                    onChange={(event) => setPolygonStrokeWidth(Number(event.target.value))}
                  />
                </div>
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Polygon fill</span>
                      <span className="mdc-range-label-short">Fill</span>
                    </span>
                    <span className="mdc-value-pill">{overlayOpacityPct}%</span>
                  </div>
                  <input
                    aria-label="Polygon fill"
                    className="mdc-range-input"
                    type="range"
                    min={OVERLAY_OPACITY_RANGE.min}
                    max={OVERLAY_OPACITY_RANGE.max}
                    value={overlayOpacityPct}
                    onChange={(event) => setOverlayOpacityPct(Number(event.target.value))}
                  />
                </div>
              </div>
            </section>

            {showAdvancedControls ? (
              <section className="mdc-control-section mdc-control-section--advanced">
                <div className="mdc-control-grid mdc-control-grid--advanced">
                  <div
                    className={`mdc-range-control${useSimilarityThreshold ? '' : ' is-disabled'}`}
                  >
                    <div className="mdc-range-header">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          checked={useSimilarityThreshold}
                          onChange={(event) => setUseSimilarityThreshold(event.target.checked)}
                        />
                        Sim &lt;
                      </label>
                      <span className="mdc-value-pill">{similarityThresholdPct}%</span>
                    </div>
                    <input
                      aria-label="Similarity highlight"
                      className="mdc-range-input"
                      type="range"
                      min={0}
                      max={100}
                      value={similarityThresholdPct}
                      disabled={!useSimilarityThreshold}
                      onChange={(event) => setSimilarityThresholdPct(Number(event.target.value))}
                    />
                  </div>
                  <div className={`mdc-range-control${useDeltaThreshold ? '' : ' is-disabled'}`}>
                    <div className="mdc-range-header">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          checked={useDeltaThreshold}
                          onChange={(event) => setUseDeltaThreshold(event.target.checked)}
                        />
                        Delta &lt;
                      </label>
                      <span className="mdc-value-pill">{deltaThresholdPct}%</span>
                    </div>
                    <input
                      aria-label="Delta highlight"
                      className="mdc-range-input"
                      type="range"
                      min={0}
                      max={100}
                      value={deltaThresholdPct}
                      disabled={!useDeltaThreshold}
                      onChange={(event) => setDeltaThresholdPct(Number(event.target.value))}
                    />
                  </div>
                  <div
                    className={`mdc-range-control${useSsmConfidenceThreshold ? '' : ' is-disabled'}`}
                  >
                    <div className="mdc-range-header">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          checked={useSsmConfidenceThreshold}
                          onChange={(event) => setUseSsmConfidenceThreshold(event.target.checked)}
                        />
                        SSM &gt;=
                      </label>
                      <span className="mdc-value-pill">{ssmConfidencePct}%</span>
                    </div>
                    <input
                      aria-label="SSM confidence filter"
                      className="mdc-range-input"
                      type="range"
                      min={SSM_CONFIDENCE_RANGE.min}
                      max={SSM_CONFIDENCE_RANGE.max}
                      value={ssmConfidencePct}
                      disabled={!useSsmConfidenceThreshold}
                      onChange={(event) => setSsmConfidencePct(Number(event.target.value))}
                    />
                  </div>
                  <div className="mdc-toggle-control-group">
                    <div className="mdc-range-control mdc-range-control--toggle-only">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          aria-label="Merge grouped candidates"
                          checked={mergeLinkedCandidates}
                          onChange={(event) => {
                            setMergeLinkedCandidates(event.target.checked);
                            updateSelectedDetectionIds(new Set());
                            setAssignmentContext(null);
                            setDetailsItem(null);
                          }}
                        />
                        <span className="mdc-toggle-label-full">Merge grouped candidates</span>
                        <span className="mdc-toggle-label-short">Merge</span>
                      </label>
                    </div>
                    <div className="mdc-range-control mdc-range-control--toggle-only">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          aria-label="Auto image area"
                          checked={autoImageArea}
                          onChange={(event) => setAutoImageArea(event.currentTarget.checked)}
                        />
                        <span className="mdc-toggle-label-full">Auto image area</span>
                        <span className="mdc-toggle-label-short">Auto img</span>
                      </label>
                    </div>
                    <div className="mdc-range-control mdc-range-control--toggle-only">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          aria-label="Auto-close on save"
                          checked={autoCloseMasterLabelOnSave}
                          onChange={(event) =>
                            setAutoCloseMasterLabelOnSave(event.currentTarget.checked)
                          }
                        />
                        <span className="mdc-toggle-label-full">Auto-close on save</span>
                        <span className="mdc-toggle-label-short">Auto close</span>
                      </label>
                    </div>
                    <div className="mdc-range-control mdc-range-control--toggle-only">
                      <label className="mdc-range-toggle">
                        <input
                          type="checkbox"
                          aria-label="Auto-select camera crops"
                          checked={autoSelectCameraCrops}
                          onChange={(event) =>
                            setAutoSelectCameraCrops(event.currentTarget.checked)
                          }
                        />
                        <span className="mdc-toggle-label-full">Auto-select camera crops</span>
                        <span className="mdc-toggle-label-short">Auto crops</span>
                      </label>
                    </div>
                  </div>
                </div>
              </section>
            ) : null}
          </div>
        ) : null}
      </div>

      {isLoading && inferenceId ? (
        <RemoteDataCollectionLoadingCard />
      ) : loadError ? (
        <div className="forge-card" style={{ padding: 18, color: 'var(--danger)' }}>
          {loadError}
        </div>
      ) : !activeImageContext && !imageIdParam ? (
        <div className="forge-card mdc-image-lookup-card">
          <form className="mdc-image-lookup-form" onSubmit={handleImageLookupSubmit}>
            <label className="mdc-image-lookup-label" htmlFor="remote-dc-image-id">
              Image ID
            </label>
            <input
              id="remote-dc-image-id"
              aria-label="Remote DC image ID"
              className="mdc-image-lookup-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={imageLookupDraft}
              onChange={(event) => {
                setImageLookupDraft(event.currentTarget.value);
                if (imageLookupError) setImageLookupError(null);
              }}
            />
            <button type="submit" className="mdc-submit-prepared">
              Open
            </button>
          </form>
          {imageLookupError ? (
            <div className="mdc-image-lookup-error">{imageLookupError}</div>
          ) : null}
        </div>
      ) : (
        <div
          className="mdc-stage-shell"
          style={
            {
              '--mdc-image-pct': imageAreaCssValue,
              '--mdc-pog-min-width': `${REMOTE_DC_MIN_POG_PANE_WIDTH}px`,
              '--mdc-crop-size': `${cropSizePx}px`,
              '--mdc-sample-crop-width': `${clamp(Math.round(cropSizePx * 0.59), 48, 190)}px`,
              '--mdc-sample-crop-height': `${clamp(Math.round(cropSizePx * 0.44), 36, 150)}px`,
            } as React.CSSProperties
          }
        >
          <div className="mdc-pair-stage">
            {shouldShowImageTimeline ? (
              <div className="forge-card mdc-image-timeline">
                <div className="mdc-image-timeline-header">
                  <div>
                    <div className="mdc-assignment-eyebrow">Same camera</div>
                    <strong>Image timeline</strong>
                  </div>
                  {isTimelineLoading ? (
                    <span className="mdc-image-timeline-status">Loading...</span>
                  ) : timelineError ? (
                    <span className="mdc-image-timeline-status mdc-image-timeline-status--error">
                      Unavailable
                    </span>
                  ) : (
                    <span className="mdc-image-timeline-status">
                      {timelineEntries.length
                        ? `${selectedTimelineIndex + 1} of ${timelineEntries.length}`
                        : 'No images'}
                    </span>
                  )}
                </div>
                {timelineError ? (
                  <div className="mdc-day-navigation-error">{timelineError}</div>
                ) : (
                  <div className="mdc-image-timeline-body">
                    <input
                      aria-label="Remote DC image timeline"
                      className="mdc-range-input mdc-image-timeline-range"
                      type="range"
                      min={0}
                      max={Math.max(0, timelineEntries.length - 1)}
                      value={selectedTimelineIndex}
                      disabled={timelineEntries.length <= 1}
                      onInput={(event) =>
                        handleTimelineIndexChange(Number(event.currentTarget.value))
                      }
                      onChange={(event) => handleTimelineIndexChange(Number(event.target.value))}
                    />
                    <div className="mdc-image-timeline-meta">
                      <strong>{timelineTimeLabel || `Image ${activeImageId ?? ''}`}</strong>
                      <span>
                        {selectedTimelineForLabel?.isCurrentDay ? 'Current day' : 'Previous image'}
                        {activeImageId ? ` · ${activeImageId}` : ''}
                      </span>
                    </div>
                    {!isTimelineLoading && timelineEntries.length === 0 ? (
                      <div className="mdc-day-navigation-empty">
                        No retained images found for this camera.
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
            <div className="mdc-direct-stage">
              <div className="forge-card mdc-pair-workspace">
                {(!activeCameraImage && (isLoading || isTimelineLoading)) ||
                (isLoadingImages && !cameraImageSrc && !cameraImageElement) ? (
                  <RemoteDataCollectionLoadingCard compact />
                ) : activeCameraImage && (cameraImageSrc || cameraImageElement) ? (
                  <>
                    {!detail ? (
                      <div className={`mdc-preview-load-bar${prLoadError ? ' is-error' : ''}`}>
                        <div>
                          <strong>
                            {prLoadError
                              ? 'PR load failed'
                              : cachedInferenceStatus === 'checking'
                                ? 'Checking cached PR'
                                : cachedInferenceStatus === 'miss'
                                  ? 'Preview only'
                                  : cachedInferenceStatus === 'error'
                                    ? 'Preview only'
                                    : 'Image preview'}
                          </strong>
                          <span>
                            {prLoadError
                              ? prLoadError
                              : isPreviewLoading
                                ? 'Loading POG context while keeping the camera image visible.'
                                : previewError
                                  ? previewError
                                  : cachedInferenceStatus === 'miss'
                                    ? 'No cached product-recognition result was found for this image.'
                                    : cachedInferenceStatus === 'error'
                                      ? 'Cached product recognition could not be loaded. You can run PR for this image.'
                                      : 'Product-recognition boxes will appear when cached PR is available.'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="mdc-submit-prepared"
                          disabled={
                            isLoadingInferenceForImage || cachedInferenceStatus === 'checking'
                          }
                          onClick={handleLoadImageInference}
                        >
                          {isLoadingInferenceForImage ? 'Loading PR...' : 'Load PR'}
                        </button>
                      </div>
                    ) : null}
                    <CameraImageViewer
                      image={cameraImageElement}
                      preloadedImage={cameraImageSrc}
                      filteredBboxes={[]}
                      boxActions={[]}
                      productRecognitionDetections={detail ? productRecognitionDetections : []}
                      planogramComplianceActions={[]}
                      showProductImages={false}
                      showFallbackImages={true}
                      onBoxClick={() => undefined}
                      onProductRecognitionClick={handleDetectionClick}
                      onBoxRightClick={handleDetectionRightClick}
                      showPogs={false}
                      currentPogs={currentPogs}
                      preloadedImages={preloadedImages}
                      preloadedImagesB64={preloadedImagesB64}
                      selectedIlItemIds={selectedIlItemIds}
                      planogramStatusMap={new Map()}
                      onPogBoxClick={handlePogBoxRightClick}
                      onPogBoxRightClick={handlePogBoxClick}
                      showShelfColumnLabels={true}
                      showEmbeddingsCount
                      imageBackgroundColor="var(--elevated)"
                      showImageFrameBorder={false}
                      fitCameraToContainerWidth
                      maxCameraImageHeight={cameraImageMaxHeight}
                      resetCameraTransformOnResize
                      cameraTransformResetKey={`${imageAreaCssValue}:${cameraImageMaxHeight}`}
                      fallbackPogBoxColor="#64748b"
                      selectedPogBoxColor="#2ea043"
                      titleActions={
                        <div className="mdc-camera-title-actions">
                          <DetectionTypeVisibilityControls
                            selectedType={visibleDetectionType}
                            onSelectType={setVisibleDetectionType}
                          />
                        </div>
                      }
                    />
                  </>
                ) : (
                  <div style={{ padding: 12, color: 'var(--text-secondary)' }}>
                    {activeCameraImage
                      ? 'Camera image failed to load from CDN.'
                      : 'Image context is unavailable for this Remote DC image.'}
                  </div>
                )}
              </div>
              <aside
                className={`forge-card mdc-direct-workspace${
                  rightPaneMode === 'master-label' ? ' mdc-direct-workspace--master-label' : ''
                }`}
              >
                {rightPaneMode === 'master-label' && focusedDraft ? (
                  <>
                    <div className="mdc-direct-pane-header mdc-direct-pane-header--actions-only">
                      <div>
                        <h2>Master Label</h2>
                      </div>
                      <div className="mdc-direct-actions">
                        {selectedDetectionIds.size > 0 ? (
                          <button
                            type="button"
                            className="mdc-assignment-secondary-action"
                            onClick={handlePrepareCurrent}
                          >
                            Load selected ({selectedDetectionIds.size})
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="mdc-modal-close"
                          aria-label="Back to POG"
                          title="Back to POG"
                          onClick={requestCloseFocusedDraft}
                        >
                          <CloseOutlinedIcon style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </div>
                    {draftErrors[focusedDraft.id] ? (
                      <div className="mdc-direct-conflict">{draftErrors[focusedDraft.id]}</div>
                    ) : null}
                    <RemoteDCPreparedMatchCard
                      key={focusedDraft.id}
                      draft={focusedDraft}
                      item={focusedDraftItem}
                      isLoading={focusedDraftIsLoading}
                      detections={focusedDraftDetections}
                      cameraImageElement={cameraImageElement}
                      cameraImageFallbackSrc={cameraImageSrc}
                      cropSizePx={cropSizePx}
                      isFocused
                      saving={savingDraftIds.has(focusedDraft.id)}
                      chainId={activeChainId || detail?.chainId || chainId || 0}
                      autoSelectCameraCrops={autoSelectCameraCrops}
                      suppressedAutoSelectCropIds={
                        suppressedDraftAutoSelectCropIds[focusedDraft.id] ?? EMPTY_REMOTE_CROP_IDS
                      }
                      onFocus={() => setFocusedDraftId(focusedDraft.id)}
                      onAllowAutoSelectCrops={(cropIds) =>
                        allowDraftAutoSelectCrops(focusedDraft.id, cropIds)
                      }
                      onSuppressAutoSelectCrops={(cropIds) =>
                        suppressDraftAutoSelectCrops(focusedDraft.id, cropIds)
                      }
                      onRemoveDetections={(detectionIds) =>
                        handleRemoveDetectionsFromDraft(focusedDraft.id, detectionIds)
                      }
                      onDirtyChange={handleDraftDirtyChange}
                      onRequestClose={requestCloseFocusedDraft}
                      onSubmit={(placements) =>
                        void handleSubmitPreparedDraft(focusedDraft.id, placements)
                      }
                      onImageHover={handleImageHover}
                      onImageMove={handleImageMove}
                      onImageLeave={handleImageLeave}
                    />
                  </>
                ) : (
                  <>
                    <RemoteDCPogPane
                      currentPogs={currentPogs}
                      preloadedImages={preloadedImages}
                      preloadedImagesB64={preloadedImagesB64}
                      selectedIlItemIds={selectedIlItemIds}
                      hiddenPogImageKeys={hiddenPogImageKeys}
                      onTogglePogImage={handleTogglePogImage}
                      onPogBoxClick={handlePogBoxRightClick}
                      onPogBoxRightClick={handlePogBoxClick}
                      showEmbeddingsCount
                      showFallbackImages
                      imageBackgroundColor="var(--elevated)"
                      showImageFrameBorder={false}
                      fallbackPogBoxColor="#64748b"
                      selectedPogBoxColor="#2ea043"
                      maxPogImageHeight={cameraImageMaxHeight}
                    />
                    {detailsItem?.kind === 'pog' ? (
                      <PogProductDetailsPanel
                        item={detailsItem}
                        chainId={activeChainId}
                        onImageHover={handleImageHover}
                        onImageMove={handleImageMove}
                        onImageLeave={handleImageLeave}
                        onClose={() => setDetailsItem(null)}
                      />
                    ) : null}
                  </>
                )}
              </aside>
              {detailsItem?.kind === 'detection' ? (
                <MasterDataCollectionDetailsModal
                  item={detailsItem}
                  storeName={activeImageContext?.storeName ?? null}
                  cameraImageElement={cameraImageElement}
                  cameraImageBase64={cameraCropSource}
                  chainId={activeChainId}
                  selectedDetectionCount={selectedDetections.length}
                  isAssigningCandidate={savingDraftIds.size > 0}
                  onAssignCandidate={handleAssignCandidate}
                  onImageHover={handleImageHover}
                  onImageMove={handleImageMove}
                  onImageLeave={handleImageLeave}
                  onClose={() => setDetailsItem(null)}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
      {pendingDiscardDraft ? (
        <div
          className="mdc-modal-backdrop"
          data-testid="remote-dc-discard-draft-dialog"
          onClick={keepEditingPendingDiscardDraft}
        >
          <div
            className="mdc-modal mdc-discard-draft-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remote-dc-discard-draft-title"
            aria-describedby="remote-dc-discard-draft-description"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="mdc-modal-header">
              <div>
                <h2 id="remote-dc-discard-draft-title">Discard Master Label changes?</h2>
                <p id="remote-dc-discard-draft-description">
                  Unsaved camera crop placements for {pendingDiscardDraft.context.label} will be
                  removed from this Master Label draft.
                </p>
              </div>
              <button
                type="button"
                className="mdc-modal-close"
                aria-label="Keep editing"
                onClick={keepEditingPendingDiscardDraft}
              >
                <CloseOutlinedIcon style={{ fontSize: 18 }} />
              </button>
            </header>
            <div className="mdc-modal-actions">
              <button
                type="button"
                className="mdc-assignment-secondary-action"
                onClick={keepEditingPendingDiscardDraft}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="mdc-assignment-secondary-action mdc-assignment-secondary-action--primary"
                onClick={confirmPendingDiscardDraft}
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {ZoomOverlay}
    </div>
  );
};
